import { toast } from "sonner";
import { confirmDialog as confirm } from "@/components/ConfirmProvider";
import React, { useEffect, useState } from "react";
import CourseProgressPanel from "@/components/wishire/CourseProgressPanel";
import CollegeCertificateSettings from "@/components/wishire/CollegeCertificateSettings";
import { openCandidateReports } from "@/lib/candidateReport";
import { ForecastPreview, ForecastCard } from "@/components/campus/ForecastPanel";
import RecruiterDrawer from "@/components/campus/RecruiterDrawer";
import { recruiterPipeline, forecastAccuracy, doneFollowup } from "@/lib/campusApi";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  createCollege, campusOverview, campusDashboard, listStudents, importStudents,
  campusSeats, setStudentLicense, requestLicenses,
  listDrives, createDrive, updateDrive, driveApplicants, updateApplication,
  campusReports, shareDrive, nudgeStudents, playbook as loadPlaybook, studentReport, driveReports,
  createAnnouncement, listAnnouncements,
  listTeam, inviteMember, removeMember, verifyStudent, setPlacementStatus,
  listDepartments, setDepartments,
  listRecruiters, addRecruiter, updateRecruiter, deleteRecruiter,
  listLeads, refreshLeads, findLeadHr, addLeadToCrm, enrichLeadEmails, draftLeadOutreach, lookupCompany, contactEmail,
  collegeInvites, respondInvite, createDriveTask, listDriveTasks, taskSubmissions,
  agentActions, agentRun, agentApprove, agentApproveAll, agentDismiss, agentAutopilot, copilotAsk, agentBriefing, agentExecute,
  setCollegeLogo, removeCollegeLogo, updateCollege, regenerateInviteCode, getMyCollege, updateCollegePolicy,
  studentProfile, listCourses, addCourse, deleteCourse, courseSales, courseProgress,
  driveAssessment, saveDriveAssessment, driveAssessmentSample, driveAssessmentResults,
  publishDriveAssessment, shortlistFromAssessment,
} from "@/lib/campusApi";
import { cellCompanyBrief, cellApproveBrief } from "@/lib/api";
import {
  GraduationCap, Users, Megaphone, CheckCircle2, AlertTriangle, ArrowRight,
  Loader2, Plus, X, Search, Award, Upload, BarChart3, Download, Check,
  Link2, Copy, Bell, ShieldCheck, Trash2, UserPlus, Building2, Layers, Printer, Mail, Target,
  RefreshCw, BookOpen, ExternalLink, Sparkles, MessageSquare, Linkedin, FileText, Mic,
  ClipboardList,
} from "lucide-react";

const APP_STAGES = ["applied", "shortlisted", "interview", "offer", "placed", "rejected"];
const STAGE_STYLE = {
  applied: "bg-stone-100 text-stone-700",
  shortlisted: "bg-blue-100 text-blue-700",
  interview: "bg-amber-100 text-amber-700",
  offer: "bg-purple-100 text-purple-700",
  placed: "bg-emerald-100 text-emerald-700",
  rejected: "bg-red-100 text-red-600",
};
const TIER_STYLE = {
  job_ready: { cls: "bg-emerald-100 text-emerald-700", label: "Job-ready" },
  developing: { cls: "bg-amber-100 text-amber-700", label: "Developing" },
  needs_work: { cls: "bg-orange-100 text-orange-700", label: "Needs work" },
  not_started: { cls: "bg-stone-200 text-stone-600", label: "Not started" },
  at_risk: { cls: "bg-red-100 text-red-600", label: "At risk" },
};

// What each Wishire-score tier means — surfaced as a legend/tooltip.
const TIER_LEGEND = [
  { label: "Job-ready", range: "70–100", dot: "#22C55E", note: "Strong résumé + interview. Ready to shortlist." },
  { label: "Developing", range: "40–69", dot: "#D97706", note: "Making progress — a nudge closes the gap." },
  { label: "Needs work", range: "1–39", dot: "#EA580C", note: "Engaged but scoring low — assign coaching." },
  { label: "Not started", range: "0", dot: "#A8A29E", note: "No résumé or mock interview yet — nudge to begin." },
];

// Downscale an image file to a ≤256px PNG data-URI — small enough to store on
// the college doc and render everywhere (student side, sidebar, PDF report).
function fileToLogoDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) { reject(new Error("Pick an image file (PNG/JPEG).")); return; }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 256;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that image.")); };
    img.src = url;
  });
}

function ScorePill({ score }) {
  const c = score >= 70 ? "#15803D" : score >= 40 ? "#D97706" : "#C2410C";
  return <span className="font-mono text-sm font-bold" style={{ color: c }}>{score}</span>;
}

/* ─────────── Onboarding ─────────── */
export function CampusOnboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [name, setName] = useState("");
  const [website, setWebsite] = useState("");
  const [logo, setLogo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const pickLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setLogo(await fileToLogoDataUrl(file)); setErr(""); }
    catch (ex) { setErr(ex.message); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("College / institution name is required."); return; }
    setBusy(true); setErr("");
    try {
      await createCollege({ name: name.trim(), website: website.trim() || undefined, logo: logo || undefined });
      await refresh();
      navigate("/campus", { replace: true });
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create workspace."); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-14">
      <div className="container-x max-w-xl">
        <span className="inline-flex p-3 rounded-2xl bg-stone-900 text-white"><GraduationCap size={22} /></span>
        <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">Set up your <span className="italic text-stone-500">placement cell.</span></h1>
        <p className="mt-3 text-stone-600">Track your students' placement readiness, run campus drives, and shortlist by an objective score.</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="cm-ob-name" placeholder="College / institution name" className="w-full px-5 py-4 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-base" />
          <input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="Website (optional)" className="w-full px-5 py-3.5 rounded-2xl bg-white border-2 border-stone-200 focus:outline-none focus:border-stone-900 text-sm" />
          <label className="flex items-center gap-4 px-5 py-3.5 rounded-2xl bg-white border-2 border-dashed border-stone-200 hover:border-stone-400 cursor-pointer transition-colors">
            {logo ? (
              <img src={logo} alt="College logo" className="w-12 h-12 rounded-xl object-contain bg-stone-50 border border-stone-200" />
            ) : (
              <span className="inline-flex p-3 rounded-xl bg-stone-100 text-stone-400"><Upload size={18} /></span>
            )}
            <div className="flex-1">
              <p className="text-sm font-medium text-stone-900">{logo ? "Logo added — click to change" : "College logo (optional)"}</p>
              <p className="text-xs text-stone-500">Shown to your students and on exported placement reports.</p>
            </div>
            <input type="file" accept="image/*" onChange={pickLogo} data-testid="cm-ob-logo" className="hidden" />
          </label>
          {err && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-2">{err}</p>}
          <button type="submit" disabled={busy} data-testid="cm-ob-submit" className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <>Create workspace <ArrowRight size={16} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─────────── Dashboard ─────────── */
const ACTION_META = {
  invite:      { icon: Building2,     cls: "bg-blue-50 border-blue-200 text-blue-800",       dot: "#0a66c2" },
  review:      { icon: Users,         cls: "bg-blue-50 border-blue-200 text-blue-800",       dot: "#0a66c2" },
  deadline:    { icon: Bell,          cls: "bg-amber-50 border-amber-200 text-amber-800",    dot: "#D97706" },
  not_started: { icon: Users,         cls: "bg-stone-50 border-stone-200 text-stone-700",    dot: "#78716C" },
  needs_work:  { icon: AlertTriangle, cls: "bg-orange-50 border-orange-200 text-orange-700", dot: "#EA580C" },
  at_risk:     { icon: AlertTriangle, cls: "bg-red-50 border-red-200 text-red-700",          dot: "#C2410C" },
  verify:      { icon: ShieldCheck,   cls: "bg-stone-50 border-stone-200 text-stone-700",    dot: "#57534E" },
};

// The AI officer's 10-second daily brief — the first thing a TPO reads.
function AiBriefing() {
  const [b, setB] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => { agentBriefing().then(({ data }) => setB(data)).catch(() => setFailed(true)); }, []);
  if (failed) return null;

  return (
    <div className="mt-8 rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-7 relative overflow-hidden wsh-grain" data-testid="ai-briefing">
      <div className="absolute inset-0 opacity-50 pointer-events-none" style={{ background: "radial-gradient(500px 280px at 8% 0%, rgba(217,119,6,0.45), transparent 60%), radial-gradient(400px 240px at 95% 100%, rgba(124,58,237,0.25), transparent 60%)" }} />
      <div className="relative">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex p-2 rounded-xl bg-amber-300 text-stone-900"><Sparkles size={15} /></span>
          <span className="overline" style={{ color: "rgba(253,251,247,0.6)" }}>Today's brief · from your AI officer</span>
        </div>
        {!b ? (
          <p className="mt-4 text-stone-400 text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> Reading your season…</p>
        ) : (
          <>
            <h2 className="font-display text-2xl md:text-3xl mt-3 leading-snug">{b.headline}</h2>
            <ul className="mt-4 grid md:grid-cols-3 gap-3">
              {(b.bullets || []).map((t, i) => (
                <li key={i} className="rounded-2xl bg-white/5 border border-white/10 px-4 py-3 text-sm text-stone-300 leading-relaxed">
                  {t}
                </li>
              ))}
            </ul>
            <Link to="/campus/ai" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-300 hover:text-amber-200">
              Open the AI officer <ArrowRight size={14} />
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function CampusDashboard() {
  const [d, setD] = useState(null);
  const [quickBusy, setQuickBusy] = useState(null);
  const [quickDone, setQuickDone] = useState(null);
  const load = () => campusDashboard().then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const quickNudge = async (tier) => {
    setQuickBusy(tier);
    try { await agentExecute("nudge_students", { tier }); setQuickDone(tier); }
    catch (_) {}
    finally { setQuickBusy(null); }
  };

  const changeLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { await setCollegeLogo(await fileToLogoDataUrl(file)); load(); } catch (_) {}
  };

  if (!d) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading your placement season…</div>;

  const s = d.season;
  const b = d.readiness.buckets;
  const notStarted = b.not_started ?? 0;
  const needsWork = b.needs_work ?? Math.max(0, (b.at_risk ?? 0) - notStarted);
  const distTotal = Math.max(1, notStarted + needsWork + b.developing + b.job_ready);

  return (
    <div className="container-x py-10 md:py-14">
      {/* ── Season header: THE number first ── */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="flex items-center gap-3">
            <label className="group relative cursor-pointer shrink-0" title={d.college_logo ? "Change logo" : "Add your college logo"}>
              {d.college_logo ? (
                <img src={d.college_logo} alt={d.college_name} data-testid="dash-college-logo" className="w-11 h-11 rounded-xl object-contain bg-white border border-stone-200" />
              ) : (
                <span className="w-11 h-11 rounded-xl bg-stone-100 border border-dashed border-stone-300 flex items-center justify-center text-stone-400 group-hover:border-stone-500 group-hover:text-stone-600 transition-colors"><GraduationCap size={18} /></span>
              )}
              <span className="absolute inset-0 rounded-xl bg-black/50 text-white text-[9px] font-mono uppercase items-center justify-center hidden group-hover:flex">{d.college_logo ? "Change" : "Add logo"}</span>
              <input type="file" accept="image/*" onChange={changeLogo} data-testid="dash-logo-input" className="hidden" />
            </label>
            <span className="overline">{d.college_name} · {d.scope_branch ? `${d.scope_branch} department` : "placement season"}</span>
          </span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">
            <span className="text-[#15803D]">{s.placement_pct}%</span> placed
            <span className="text-stone-400 text-2xl md:text-3xl"> · {s.placed} of {s.placeable ?? s.students}</span>
          </h1>
          <p className="text-stone-500 mt-2">{s.offers_in_flight} offer{s.offers_in_flight === 1 ? "" : "s"} in flight · {s.unplaced} student{s.unplaced === 1 ? "" : "s"} still to place
            {(s.higher_studies > 0 || s.opted_out > 0) && (
              <span className="text-stone-400"> · {s.students} total, {[s.higher_studies && `${s.higher_studies} higher studies`, s.opted_out && `${s.opted_out} opted out`].filter(Boolean).join(", ")} excluded</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/campus/drives" data-testid="dash-quick-drive" className="btn-primary text-sm"><Megaphone size={15} /> Post a drive</Link>
          <Link to="/campus/students" className="btn-ghost text-sm"><Upload size={15} /> Import students</Link>
          <Link to="/campus/announcements" className="btn-ghost text-sm"><Bell size={15} /> Announce</Link>
        </div>
      </div>

      {/* Season progress bar */}
      <div className="mt-5 h-2.5 rounded-full bg-stone-200 overflow-hidden" title={`${s.placement_pct}% placed`}>
        <div className="h-full rounded-full bg-gradient-to-r from-[#15803D] to-emerald-400 transition-all duration-700" style={{ width: `${s.placement_pct}%` }} />
      </div>

      {/* ── AI officer's daily brief ── */}
      <AiBriefing />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10 items-start">
        {/* ── Needs your attention ── */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl mb-4">Needs your attention</h2>
          {d.actions.length === 0 ? (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-8 flex items-center gap-3 text-emerald-800">
              <CheckCircle2 size={22} /> All clear — no pending reviews, deadlines, or at-risk alerts today.
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="dash-actions">
              {d.actions.map((a, i) => {
                const m = ACTION_META[a.kind] || ACTION_META.verify;
                // One-click AI action for nudge-able items — no page hop needed.
                const nudgeTier = a.kind === "not_started" ? "not_started" : a.kind === "needs_work" ? "needs_work" : null;
                return (
                  <div key={i} className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-sm font-medium transition-all ${m.cls}`}>
                    <m.icon size={17} style={{ color: m.dot }} />
                    <Link to={a.to} className="flex-1 hover:underline">{a.label}</Link>
                    {nudgeTier && quickDone !== nudgeTier && (
                      <button onClick={() => quickNudge(nudgeTier)} disabled={quickBusy === nudgeTier} data-testid={`dash-quick-${nudgeTier}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-900 text-white text-xs font-semibold hover:bg-stone-700 disabled:opacity-60 shrink-0">
                        {quickBusy === nudgeTier ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Nudge now
                      </button>
                    )}
                    {nudgeTier && quickDone === nudgeTier && (
                      <span className="text-xs font-semibold text-emerald-700 inline-flex items-center gap-1 shrink-0"><CheckCircle2 size={13} /> Sent</span>
                    )}
                    {!nudgeTier && <ArrowRight size={15} className="opacity-60" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Open drives ── */}
          <h2 className="font-display text-2xl mb-4 mt-10">Open drives</h2>
          {d.open_drives.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-500">
              No open drives. <Link to="/campus/drives" className="text-[#C2410C] font-medium hover:underline">Post your first drive →</Link>
            </div>
          ) : (
            <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
              {d.open_drives.map((dr, i) => (
                <Link key={dr.drive_id} to="/campus/drives" className={`flex items-center gap-4 px-5 py-4 hover:bg-stone-50 ${i > 0 ? "border-t border-stone-100" : ""}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-stone-900 truncate">{dr.title}</p>
                    <p className="text-xs text-stone-500">{dr.company_name}{dr.deadline ? ` · closes ${new Date(dr.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}</p>
                  </div>
                  {dr.pending > 0 && (
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-700 shrink-0">{dr.pending} to review</span>
                  )}
                  <span className="font-mono text-sm text-stone-500 shrink-0">{dr.applicants} applied</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* ── Right rail: readiness pulse ── */}
        <div className="space-y-6">
          <PlaybookStrip />
          <div className="rounded-3xl bg-stone-900 text-stone-50 p-6 wsh-grain relative overflow-hidden" data-testid="dash-readiness">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(320px 220px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
            <div className="relative">
              <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Batch readiness</span>
              <p className="font-display text-5xl mt-2">{d.readiness.avg}<span className="text-xl text-stone-400"> avg</span></p>
              {/* Distribution bar */}
              <div className="mt-5 flex h-3 rounded-full overflow-hidden bg-white/10">
                <div style={{ width: `${(notStarted / distTotal) * 100}%`, background: "#A8A29E" }} title={`${notStarted} not started`} />
                <div style={{ width: `${(needsWork / distTotal) * 100}%`, background: "#EA580C" }} title={`${needsWork} need work`} />
                <div style={{ width: `${(b.developing / distTotal) * 100}%`, background: "#D97706" }} title={`${b.developing} developing`} />
                <div style={{ width: `${(b.job_ready / distTotal) * 100}%`, background: "#22C55E" }} title={`${b.job_ready} job-ready`} />
              </div>
              <div className="mt-3 space-y-1.5 text-sm">
                <p className="flex justify-between" title="No résumé or mock interview yet"><span className="text-stone-300">● Not started</span><span className="font-mono">{notStarted}</span></p>
                <p className="flex justify-between" title="Engaged but scoring under 40 — assign coaching"><span className="text-orange-300">● Needs work</span><span className="font-mono">{needsWork}</span></p>
                <p className="flex justify-between" title="Score 40–69"><span className="text-amber-300">● Developing</span><span className="font-mono">{b.developing}</span></p>
                <p className="flex justify-between" title="Score 70+ — ready to shortlist"><span className="text-emerald-300">● Job-ready</span><span className="font-mono">{b.job_ready}</span></p>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-stone-400">
                The Wishire score blends résumé, mock interviews, skills &amp; communication into one 0–100 readiness index.
              </p>
              {(notStarted + needsWork) > 0 && (
                <Link to="/campus/students" className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-amber-300 hover:text-amber-200">
                  Nudge students who need help <ArrowRight size={14} />
                </Link>
              )}
            </div>
          </div>

          {/* Invite code */}
          <div className="rounded-3xl border border-stone-200 bg-white p-5">
            <span className="overline">Student invite code</span>
            <p className="font-mono text-2xl tracking-[0.2em] mt-2 text-stone-900">{d.invite_code}</p>
            <p className="text-xs text-stone-500 mt-1.5">Share with students so they join your college at signup.</p>
          </div>

          {/* Latest announcements */}
          {d.announcements.length > 0 && (
            <div className="rounded-3xl border border-stone-200 bg-white p-5">
              <span className="overline">Latest announcements</span>
              <div className="mt-3 space-y-3">
                {d.announcements.map((a) => (
                  <div key={a.id}>
                    <p className="text-sm font-medium text-stone-900">{a.title}</p>
                    <p className="text-xs text-stone-400">{a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
function Stat({ icon: Icon, label, value, to, accent }) {
  return (
    <a href={to} className="group rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.3)] transition-all">
      <span className="inline-flex p-2.5 rounded-xl mb-4" style={{ background: `${accent}15`, color: accent }}><Icon size={20} /></span>
      <p className="font-display text-4xl">{value}</p>
      <p className="overline mt-1">{label}</p>
    </a>
  );
}

/* The three steering numbers, one glance, link to the playbook. */
function PlaybookStrip() {
  const [p, setP] = useState(null);
  useEffect(() => { loadPlaybook().then(({ data }) => setP(data)).catch(() => {}); }, []);
  if (!p) return null;
  const m = p.metrics, ref = p.last_week.available ? p.last_week : p.baseline;
  const tone = m.band.key === "crisis" ? "text-red-700" : m.band.key === "average" ? "text-amber-700" : "text-emerald-700";
  return (
    <Link to="/campus/playbook" className="block rounded-3xl border border-stone-200 bg-white p-5 hover:border-stone-900 transition-colors" data-testid="dash-playbook-strip">
      <div className="flex items-center justify-between"><span className="overline">This week</span><span className="text-xs text-stone-500 inline-flex items-center gap-1">Playbook <ArrowRight size={12} /></span></div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div><p className={`font-display text-3xl leading-none ${tone}`}>{m.readiness_pct}%</p><p className="text-[11px] text-stone-500 mt-1">ready{ref.available && ref.readiness_delta ? <span className={ref.readiness_delta > 0 ? " text-emerald-700" : " text-red-700"}> {ref.readiness_delta > 0 ? "+" : ""}{ref.readiness_delta}</span> : ""}</p></div>
        <div><p className={`font-display text-3xl leading-none ${m.coverage_ok ? "text-emerald-700" : "text-amber-700"}`}>{m.coverage_pct}%</p><p className="text-[11px] text-stone-500 mt-1">covered</p></div>
        <div><p className="font-display text-3xl leading-none text-stone-900">{m.tiers.B.count}</p><p className="text-[11px] text-stone-500 mt-1">middle band</p></div>
      </div>
      {p.actions[0] && <p className="mt-3 text-xs text-stone-600 leading-relaxed"><Target size={11} className="inline mr-1 text-stone-400" />{p.actions[0]}</p>}
    </Link>
  );
}

/* ─────────── Students (readiness) ─────────── */
export function CampusStudents() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [seats, setSeats] = useState(null);
  const [showBuySeats, setShowBuySeats] = useState(false);
  const loadSeats = () => campusSeats().then(({ data }) => setSeats(data)).catch(() => {});
  useEffect(() => { loadSeats(); }, []); // eslint-disable-line
  const [showDepts, setShowDepts] = useState(false);
  const [showCourses, setShowCourses] = useState(false);
  const [viewing, setViewing] = useState(null); // student user_id for the 360° drawer
  const [nudging, setNudging] = useState(false);
  const [nudgeMsg, setNudgeMsg] = useState(null);
  const [tierFilter, setTierFilter] = useState("all");
  const [selected, setSelected] = useState([]);
  const load = (branch) => listStudents(branch).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const atRiskCount = (rows || []).filter((s) => s.tier === "at_risk" || s.tier === "not_started" || s.tier === "needs_work").length;
  const filtered = (rows || []).filter((s) => tierFilter === "all" || s.tier === tierFilter);
  const toggle = (id) => setSelected((sel) => sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]);
  const allSelected = filtered.length > 0 && filtered.every((s) => selected.includes(s.user_id));
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map((s) => s.user_id));

  const nudgeAtRisk = async () => {
    setNudging(true); setNudgeMsg(null);
    try {
      const { data } = await nudgeStudents(); // no ids → all at-risk
      setNudgeMsg(`Sent a prep nudge to ${data.nudged} at-risk student${data.nudged === 1 ? "" : "s"}.`);
    } catch (e) { setNudgeMsg(e?.response?.data?.detail || "Could not send nudges."); }
    finally { setNudging(false); }
  };
  const nudgeSelected = async () => {
    setNudging(true); setNudgeMsg(null);
    try {
      const { data } = await nudgeStudents(selected);
      setNudgeMsg(`Sent a prep nudge to ${data.nudged} selected student${data.nudged === 1 ? "" : "s"}.`);
      setSelected([]);
    } catch (e) { setNudgeMsg(e?.response?.data?.detail || "Could not send nudges."); }
    finally { setNudging(false); }
  };

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="overline">Students</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Your batch, ranked by readiness.</h1>
          {seats && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="cm-seats">
              <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${seats.available > 0 ? "border-stone-300 bg-white text-stone-700" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                <Users size={12} /> {seats.active}/{seats.licensed} licensed seats used
              </span>
              {seats.locked > 0 && (
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-red-200 bg-red-50 text-red-700">
                  <Lock size={12} /> {seats.locked} student{seats.locked === 1 ? "" : "s"} locked
                </span>
              )}
              {(seats.locked > 0 || seats.available === 0) && (
                <button onClick={() => setShowBuySeats(true)} data-testid="cm-buy-seats"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412]">
                  Buy more licenses
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 max-w-full">
          {atRiskCount > 0 && (
            <button onClick={nudgeAtRisk} disabled={nudging} data-testid="cm-nudge-atrisk"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-amber-300 text-amber-800 bg-amber-50 text-sm font-medium hover:bg-amber-100 disabled:opacity-60">
              {nudging ? <Loader2 size={15} className="animate-spin" /> : <Bell size={15} />} Nudge {atRiskCount} who need help
            </button>
          )}
          <button onClick={() => setShowCourses(true)} data-testid="cm-courses-btn" className="btn-ghost"><BookOpen size={16} /> Courses</button>
          <button onClick={() => setShowDepts(true)} data-testid="cm-depts-btn" className="btn-ghost"><Layers size={16} /> Departments</button>
          <button onClick={() => setShowImport(true)} data-testid="cm-import-btn" className="btn-primary"><Upload size={16} /> Import students</button>
        </div>
      </div>
      {nudgeMsg && <p className="mt-4 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5">{nudgeMsg}</p>}
      {showImport && <ImportModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); loadSeats(); }} />}
      {showBuySeats && <BuySeatsModal seats={seats} onClose={() => setShowBuySeats(false)} />}
      {showDepts && <DepartmentsModal onClose={() => setShowDepts(false)} />}
      {showCourses && <CoursesModal onClose={() => setShowCourses(false)} />}
      {viewing && <StudentDrawer studentId={viewing} onClose={() => setViewing(null)} />}
      <div className="mt-6 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q)}
            placeholder="Filter by branch… (Enter)" className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900" />
        </div>
        <div className="flex items-center gap-1.5">
          {[["all", "All"], ["job_ready", "Job-ready"], ["developing", "Developing"], ["needs_work", "Needs work"], ["not_started", "Not started"]].map(([k, label]) => (
            <button key={k} onClick={() => setTierFilter(k)} data-testid={`cm-tier-${k}`}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${tierFilter === k ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:bg-stone-100"}`}>
              {label}
            </button>
          ))}
        </div>
        {selected.length > 0 && (
          <button onClick={nudgeSelected} disabled={nudging} data-testid="cm-nudge-selected"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C2410C] text-white text-sm font-medium hover:bg-[#9a3412] disabled:opacity-60">
            {nudging ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} />} Nudge {selected.length} selected
          </button>
        )}
      </div>

      {/* Score legend — what each status means */}
      <div className="mb-5 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-2xl border border-stone-200 bg-[#F5F3ED] px-4 py-3 text-xs text-stone-600">
        <span className="font-mono uppercase tracking-widest text-stone-400">What the status means</span>
        {TIER_LEGEND.map((t) => (
          <span key={t.label} className="inline-flex items-center gap-1.5" title={t.note}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.dot }} />
            <span className="font-medium text-stone-800">{t.label}</span>
            <span className="font-mono text-stone-400">{t.range}</span>
          </span>
        ))}
      </div>

      {rows === null ? <p className="text-stone-500">Loading…</p> : filtered.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">
          {rows.length === 0 ? "No students yet. Share your placement cell's invite code (in the sidebar) with students so they can join." : "No students match this filter."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
              <th className="px-4 py-3 w-10"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="cm-select-all" className="w-4 h-4 accent-[#C2410C]" /></th>
              {["Student", "Branch", "Section", "CGPA", "Grad yr", "Readiness", "Status", "Placement", "Verified"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.map((s) => {
                const t = TIER_STYLE[s.tier] || TIER_STYLE.developing;
                return (
                  <tr key={s.user_id} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(s.user_id)} onChange={() => toggle(s.user_id)} data-testid={`cm-select-${s.user_id}`} className="w-4 h-4 accent-[#C2410C]" /></td>
                    <td className="px-4 py-3">
                      <button onClick={() => setViewing(s.user_id)} data-testid={`cm-view-${s.user_id}`} className="text-left group">
                        <p className={`font-medium group-hover:underline ${s.license_active ? "text-stone-900" : "text-stone-400"}`}>
                          {s.name}
                          {!s.license_active && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200"><Lock size={9} /> LOCKED</span>}
                        </p>
                        <p className="text-xs text-stone-500">{s.email}</p>
                      </button>
                      {!s.license_active && (
                        <button onClick={async () => {
                          try { await setStudentLicense(s.user_id, true); load(q); loadSeats(); }
                          catch (e) { toast.error(e?.response?.data?.detail || "No seats left."); }
                        }} data-testid={`cm-activate-${s.user_id}`}
                          className="mt-1 text-[11px] font-medium text-emerald-700 hover:underline">
                          Activate seat &rarr;
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-stone-600">{s.branch || "—"}</td>
                    <td className="px-4 py-3 text-stone-600" data-testid={`cm-section-${s.user_id}`}>{s.section || "—"}</td>
                    <td className="px-4 py-3 text-stone-600">{s.cgpa ?? "—"}</td>
                    <td className="px-4 py-3 text-stone-600">{s.grad_year ?? "—"}</td>
                    <td className="px-4 py-3"><ScorePill score={s.wishire_score} /></td>
                    <td className="px-4 py-3"><span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${t.cls}`}>{t.label}</span></td>
                    <td className="px-4 py-3"><PlacementStatusSelect student={s} onChange={() => load(q)} /></td>
                    <td className="px-4 py-3"><VerifyToggle student={s} onChange={() => load(q)} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────── Drives ─────────── */
/* Incoming company hiring invites — one click turns one into a live drive. */
function InvitesSection({ onAccepted }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = () => collegeInvites().then(({ data }) => setData(data)).catch(() => setData({ pending: [] }));
  useEffect(() => { load(); }, []);

  const respond = async (inv, accept) => {
    setBusy(inv.invite_id);
    try {
      await respondInvite(inv.invite_id, accept);
      setMsg(accept ? `${inv.company_name}'s drive is now LIVE on your campus — students can apply.` : `Invite from ${inv.company_name} declined.`);
      load(); if (accept) onAccepted?.();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not respond."); }
    finally { setBusy(null); }
  };

  const pending = data?.pending || [];
  if (!data || (pending.length === 0 && !msg)) return null;

  return (
    <div className="mb-8" data-testid="cm-invites">
      {pending.length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-1 flex items-center gap-2">
            <span className="inline-flex p-1.5 rounded-lg bg-stone-900 text-amber-300"><Building2 size={15} /></span>
            Companies want to hire from you
          </h2>
          <p className="text-sm text-stone-500 mb-4">Accepting an invite opens it as a live drive instantly — the company sees your pipeline in real time.</p>
        </>
      )}
      {msg && <p className="mb-3 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5" data-testid="cm-invite-msg">{msg}</p>}
      <div className="space-y-2.5">
        {pending.map((inv) => (
          <div key={inv.invite_id} className="rounded-2xl border border-blue-200 bg-blue-50/60 px-5 py-4" data-testid={`cm-invite-${inv.invite_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-900">{inv.company_name} · {inv.title}</p>
                <p className="text-xs text-stone-600">{inv.role}{inv.ctc ? ` · ${inv.ctc}` : ""}{inv.deadline ? ` · apply by ${inv.deadline}` : ""}
                  {(inv.eligibility?.branches || []).length > 0 && ` · ${inv.eligibility.branches.join(", ")}`}
                  {inv.eligibility?.min_wishire_score != null && ` · score ≥ ${inv.eligibility.min_wishire_score}`}
                </p>
                {inv.message && <p className="mt-1 text-xs text-stone-500 italic">"{inv.message}"</p>}
              </div>
              <div className="flex flex-wrap gap-2 max-w-full">
                <button onClick={() => respond(inv, false)} disabled={busy === inv.invite_id}
                  className="text-xs px-3 py-1.5 rounded-full border border-stone-300 text-stone-500 hover:border-stone-900 hover:text-stone-900 disabled:opacity-60">
                  Decline
                </button>
                <button onClick={() => respond(inv, true)} disabled={busy === inv.invite_id} data-testid={`cm-invite-accept-${inv.invite_id}`}
                  className="text-xs font-medium px-4 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60">
                  {busy === inv.invite_id ? <Loader2 size={12} className="animate-spin inline" /> : "Accept — go live"}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* Hiring-round tasks on a drive: create + review submissions. */
function TasksModal({ drive, onClose }) {
  const [tasks, setTasks] = useState(null);
  const [f, setF] = useState({ title: "", instructions: "", due: "" });
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null); // {task, submissions}
  const [msg, setMsg] = useState(null);
  const load = () => listDriveTasks(drive.drive_id).then(({ data }) => setTasks(data)).catch(() => setTasks([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.instructions.trim()) { setMsg("Title and instructions are required."); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await createDriveTask(drive.drive_id, { title: f.title.trim(), instructions: f.instructions.trim(), due: f.due || undefined });
      setMsg(`Task published — ${data.emailed} applicant${data.emailed === 1 ? "" : "s"} emailed.`);
      setF({ title: "", instructions: "", due: "" }); load();
    } catch (e2) { setMsg(e2?.response?.data?.detail || "Could not create task."); }
    finally { setBusy(false); }
  };

  const openSubs = async (t) => {
    try { const { data } = await taskSubmissions(t.task_id); setViewing(data); } catch (_) {}
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div><h2 className="font-display text-2xl">Hiring tasks — {drive.title}</h2><p className="text-sm text-stone-500">Assignments/case rounds for applicants. Students submit from their campus page.</p></div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5">
          {viewing ? (
            <div>
              <button onClick={() => setViewing(null)} className="text-sm text-stone-500 hover:text-stone-900 mb-3">← Back to tasks</button>
              <h3 className="font-display text-xl">{viewing.task.title}</h3>
              <p className="text-sm text-stone-500 mb-4">{viewing.submissions.length} submission{viewing.submissions.length === 1 ? "" : "s"}</p>
              {viewing.submissions.length === 0 ? (
                <p className="text-sm text-stone-500 py-6 text-center">No submissions yet.</p>
              ) : (
                <div className="space-y-2">
                  {viewing.submissions.map((s, i) => (
                    <div key={i} className="rounded-xl border border-stone-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{s.student?.name} <span className="text-xs text-stone-400">· {s.student?.branch || "—"} · score {s.student?.wishire_score ?? "—"}</span></p>
                        {s.note && <p className="text-xs text-stone-500 truncate">{s.note}</p>}
                      </div>
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-xs font-medium px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 shrink-0 inline-flex items-center gap-1">
                        Open <ExternalLink size={11} />
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <form onSubmit={create} className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3">
                <div className="grid grid-cols-[1fr_auto] gap-3">
                  <Field label="Task title *"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="task-title" placeholder="Build a REST API for a todo app" className="cm-inp" /></Field>
                  <Field label="Due"><input type="date" value={f.due} onChange={(e) => setF({ ...f, due: e.target.value })} className="cm-inp" /></Field>
                </div>
                <Field label="Instructions *"><textarea value={f.instructions} onChange={(e) => setF({ ...f, instructions: e.target.value })} data-testid="task-instructions" rows={3} placeholder="What should applicants build/solve, and how to submit (GitHub link, doc…)" className="cm-inp" /></Field>
                {msg && <p className="text-sm text-stone-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="task-msg">{msg}</p>}
                <button type="submit" disabled={busy} data-testid="task-create" className="btn-primary w-full justify-center disabled:opacity-60">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Publish task (emails all applicants)</>}
                </button>
              </form>
              <div className="mt-4 space-y-2">
                {tasks === null ? <p className="text-sm text-stone-500">Loading…</p> : tasks.length === 0 ? (
                  <p className="text-sm text-stone-500 text-center py-4">No tasks yet.</p>
                ) : tasks.map((t) => (
                  <div key={t.task_id} className="rounded-xl border border-stone-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{t.title}{t.due && <span className="text-xs text-[#C2410C] ml-2">due {t.due}</span>}</p>
                      <p className="text-xs text-stone-500 truncate">{t.instructions}</p>
                    </div>
                    <button onClick={() => openSubs(t)} data-testid={`task-subs-${t.task_id}`} className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900 shrink-0">
                      {t.submissions} submission{t.submissions === 1 ? "" : "s"} →
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function CampusDrives() {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [applicantsFor, setApplicantsFor] = useState(null);
  const [tasksFor, setTasksFor] = useState(null);
  const [assessFor, setAssessFor] = useState(null);
  const [briefFor, setBriefFor] = useState(null);
  const load = () => listDrives().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline">Drives</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Campus placement drives.</h1>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="cm-add-drive" className="btn-primary shrink-0"><Plus size={16} /> New drive</button>
      </div>

      <InvitesSection onAccepted={load} />

      {showAdd && <DriveModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      <ForecastAccuracyCard />
      {applicantsFor && <ApplicantsModal drive={applicantsFor} onClose={() => setApplicantsFor(null)} />}
      {tasksFor && <TasksModal drive={tasksFor} onClose={() => setTasksFor(null)} />}
      {assessFor && <AssessmentModal drive={assessFor} onClose={() => setAssessFor(null)} />}

      {rows === null ? <p className="text-stone-500">Loading…</p> : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">No drives yet. Post your first campus drive.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rows.map((d) => {
            const e = d.eligibility || {};
            return (
              <div key={d.drive_id} className="rounded-3xl border border-stone-200 bg-white p-6 flex flex-col">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{d.company_name}</p>
                    <p className="font-display text-2xl mt-1">{d.title}</p>
                    <p className="text-sm text-stone-500">{d.role}{d.ctc ? ` · ${d.ctc}` : ""}</p>
                  </div>
                  <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-full ${d.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{d.status}</span>
                </div>
                {d.deadline && <p className="mt-2 text-xs text-stone-500">Apply by {new Date(d.deadline).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>}
                <div className="mt-4 flex flex-wrap gap-1.5 text-xs">
                  {e.min_cgpa != null && <span className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">CGPA ≥ {e.min_cgpa}</span>}
                  {e.min_wishire_score != null && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Score ≥ {e.min_wishire_score}</span>}
                  {(e.branches || []).map((b) => <span key={b} className="px-2 py-0.5 rounded-full bg-stone-100 text-stone-600">{b}</span>)}
                </div>
                <div className="mt-5 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setApplicantsFor(d)} className="text-sm font-medium text-[#C2410C] hover:underline">{d.applicants_count} applicant{d.applicants_count === 1 ? "" : "s"} →</button>
                    <button onClick={() => setTasksFor(d)} data-testid={`cm-tasks-${d.drive_id}`} className="text-xs font-medium text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"><ClipboardList size={12} /> Tasks</button>
                    <button onClick={() => setAssessFor(d)} data-testid={`cm-assess-${d.drive_id}`} className="text-xs font-medium text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"><ShieldCheck size={12} /> Assessment</button>
                    <button onClick={() => setBriefFor(d)} data-testid={`cm-brief-${d.drive_id}`}
                      className={`text-xs font-medium inline-flex items-center gap-1 ${d.company_brief_approved ? "text-emerald-700" : "text-stone-500 hover:text-stone-900"}`}>
                      <BookOpen size={12} /> Brief{d.company_brief_approved ? " ✓" : ""}
                    </button>
                  </div>
                  <select value={d.status} onChange={async (e2) => { await updateDrive(d.drive_id, e2.target.value); load(); }}
                    className="text-xs px-2 py-1 rounded-full border border-stone-300 bg-white cursor-pointer">
                    {["open", "closed", "draft"].map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                </div>
                <ForecastCard drive={d} />
                <RecruiterLinkButton drive={d} />
                {briefFor?.drive_id === d.drive_id && (
                  <CellBriefModal drive={d} onClose={() => setBriefFor(null)} onChanged={load} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* Drive Assessment: attach a proctored screening test to a drive, preview a
   sample paper, watch ranked integrity-verified results, shortlist in one click. */
function AssessmentModal({ drive, onClose }) {
  const [tab, setTab] = useState("setup"); // setup | results
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ template: "role", pattern: "tcs_nqt", window_end: "", cutoff: 60 });
  const [saving, setSaving] = useState(false);
  const [sample, setSample] = useState(null);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => driveAssessment(drive.drive_id).then(({ data }) => {
    setData(data);
    if (data.exists) {
      setForm({
        template: data.template.startsWith("company:") ? "company" : data.template,
        pattern: data.template.startsWith("company:") ? data.template.split(":")[1] : "tcs_nqt",
        window_end: (data.window_end || "").slice(0, 16),
        cutoff: data.cutoff ?? 60,
      });
    }
  }).catch((e) => setMsg(e?.response?.data?.detail || "Failed to load."));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    if (!form.window_end) { setMsg("Set the window end date/time."); return; }
    setSaving(true); setMsg("");
    try {
      const template = form.template === "company" ? `company:${form.pattern}` : form.template;
      await saveDriveAssessment(drive.drive_id, { template, window_end: new Date(form.window_end).toISOString(), cutoff: Number(form.cutoff) });
      setMsg("Assessment saved — applicants can now sit it from their drive card.");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Failed to save."); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    setSampleBusy(true); setSample(null);
    try { const { data } = await driveAssessmentSample(drive.drive_id); setSample(data); }
    catch (e) { setMsg(e?.response?.data?.detail || "Save the assessment first, then preview."); }
    finally { setSampleBusy(false); }
  };

  const loadResults = async () => {
    setResults(null);
    try { const { data } = await driveAssessmentResults(drive.drive_id); setResults(data); }
    catch (e) { setMsg(e?.response?.data?.detail || "No assessment configured yet."); }
  };

  const doShortlist = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    setBusy(true);
    try { const { data } = await shortlistFromAssessment(drive.drive_id, ids); setMsg(`${data.shortlisted} student(s) moved to Shortlisted.`); setSelected({}); }
    catch (e) { setMsg(e?.response?.data?.detail || "Shortlist failed."); }
    finally { setBusy(false); }
  };

  const togglePublish = async () => {
    setBusy(true);
    try { await publishDriveAssessment(drive.drive_id, !(results?.assessment?.published)); await loadResults(); }
    catch (_) {} finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="w-full max-w-3xl rounded-3xl bg-white overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="assessment-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-white z-10">
          <div>
            <h2 className="font-display text-xl">Drive assessment — {drive.company_name}</h2>
            <p className="text-xs text-stone-500">{drive.title} · proctored screening with AI integrity review</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>

        <div className="px-6 pt-4 flex gap-2">
          {["setup", "results"].map((t) => (
            <button key={t} onClick={() => { setTab(t); setMsg(""); if (t === "results") loadResults(); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize ${tab === t ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
              {t}
            </button>
          ))}
        </div>

        {msg && <p className="mx-6 mt-3 text-sm rounded-xl bg-amber-50 border border-amber-200 text-amber-800 px-4 py-2">{msg}</p>}

        {tab === "setup" && (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="text-xs font-medium text-stone-500">Paper template</label>
              <div className="mt-1.5 grid sm:grid-cols-2 gap-2">
                <label className={`rounded-xl border px-4 py-3 text-sm cursor-pointer ${form.template === "role" ? "border-stone-900 bg-stone-50" : "border-stone-200"}`}>
                  <input type="radio" className="mr-2 accent-[#C2410C]" checked={form.template === "role"} onChange={() => setForm({ ...form, template: "role" })} />
                  <b>Role-based</b><span className="block text-xs text-stone-500 mt-0.5 ml-5">Aptitude + role knowledge + verbal, generated for "{drive.role || drive.title}".</span>
                </label>
                <label className={`rounded-xl border px-4 py-3 text-sm cursor-pointer ${form.template === "company" ? "border-stone-900 bg-stone-50" : "border-stone-200"}`}>
                  <input type="radio" className="mr-2 accent-[#C2410C]" checked={form.template === "company"} onChange={() => setForm({ ...form, template: "company" })} />
                  <b>Company pattern</b><span className="block text-xs text-stone-500 mt-0.5 ml-5">Mirror a known recruiter's real test structure.</span>
                </label>
              </div>
              {form.template === "company" && (
                <select value={form.pattern} onChange={(e) => setForm({ ...form, pattern: e.target.value })}
                  className="mt-2 w-full max-w-sm px-3 py-2 rounded-xl border border-stone-300 text-sm">
                  {(data?.patterns || []).map((p) => <option key={p.id} value={p.id}>{p.company} — {p.name}</option>)}
                </select>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-stone-500">Window closes</label>
                <input type="datetime-local" value={form.window_end} onChange={(e) => setForm({ ...form, window_end: e.target.value })}
                  data-testid="assess-window" className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-500">Cutoff (%)</label>
                <input type="number" min="0" max="100" value={form.cutoff} onChange={(e) => setForm({ ...form, cutoff: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-xl border border-stone-300 text-sm font-mono" />
              </div>
            </div>
            <div className="rounded-xl bg-stone-50 border border-stone-200 p-3 text-xs text-stone-600">
              <b>Guardrails, automatic:</b> every applicant gets a <b>unique fresh paper</b> (same structure) — sharing answers is useless. Camera required, snapshots every 30s, AI proctor checks for second person / phone, fullscreen + tab + copy/paste violations timestamped. One attempt per student. Results stay hidden until you publish.
            </div>
            <div className="flex items-center gap-3">
              <button onClick={save} disabled={saving} data-testid="assess-save" className="btn-primary text-sm disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {data?.exists ? "Update assessment" : "Attach assessment"}
              </button>
              <button onClick={preview} disabled={sampleBusy || !data?.exists} className="text-sm font-medium text-[#C2410C] hover:underline disabled:opacity-40">
                {sampleBusy ? "Generating sample…" : "Preview a sample paper"}
              </button>
              {data?.exists && <span className="text-xs text-stone-500">{data.graded_count} submitted · {data.in_progress_count} in progress</span>}
            </div>
            {sample && (
              <div className="rounded-2xl border border-stone-200 p-4 max-h-72 overflow-y-auto">
                <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-1.5 inline-block">{sample.note}</p>
                {sample.sections.map((s) => (
                  <div key={s.key} className="mt-3">
                    <p className="text-sm font-semibold">{s.label}</p>
                    {s.mcq.slice(0, 3).map((q, i) => (
                      <p key={i} className="text-xs text-stone-600 mt-1.5 whitespace-pre-wrap">• {q.q} <span className="text-emerald-700 font-medium">→ {q.options[q.answer_index]}</span></p>
                    ))}
                    {s.mcq.length > 3 && <p className="text-[11px] text-stone-400 mt-1">…and {s.mcq.length - 3} more in this section</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "results" && (
          <div className="px-6 py-5">
            {!results ? <p className="text-sm text-stone-500 py-6">Loading results…</p> : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <p className="text-sm text-stone-600">{results.attempted_count} of {results.applied_count} applicants attempted · cutoff {results.assessment.cutoff}%</p>
                  <div className="flex items-center gap-2">
                    <button onClick={togglePublish} disabled={busy} className={`text-xs font-medium px-3.5 py-2 rounded-full border ${results.assessment.published ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
                      {results.assessment.published ? "Results published ✓" : "Publish results to students"}
                    </button>
                    <button onClick={doShortlist} disabled={busy || !Object.values(selected).some(Boolean)} data-testid="assess-shortlist"
                      className="text-xs font-semibold px-3.5 py-2 rounded-full bg-[#C2410C] text-white hover:bg-[#9a3412] disabled:opacity-40">
                      Shortlist selected
                    </button>
                  </div>
                </div>
                {results.rows.length === 0 ? <p className="text-sm text-stone-500 py-4">No submissions yet.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-stone-400 border-b border-stone-200">
                        <th className="py-2 pr-2"></th><th className="py-2 pr-3">Student</th><th className="py-2 pr-3">Score</th>
                        <th className="py-2 pr-3">Sections</th><th className="py-2 pr-3">Integrity</th><th className="py-2">Violations</th>
                      </tr></thead>
                      <tbody>
                        {results.rows.map((r) => (
                          <tr key={r.student_id} className="border-b border-stone-100 align-top">
                            <td className="py-2.5 pr-2"><input type="checkbox" checked={!!selected[r.student_id]} onChange={(e) => setSelected((s) => ({ ...s, [r.student_id]: e.target.checked }))} className="accent-[#C2410C]" /></td>
                            <td className="py-2.5 pr-3"><p className="font-medium">{r.name}</p><p className="text-[11px] text-stone-400">{r.branch}{r.cgpa ? ` · CGPA ${r.cgpa}` : ""}</p></td>
                            <td className="py-2.5 pr-3"><span className={`font-mono font-bold ${r.band_pass ? "text-emerald-600" : "text-red-600"}`}>{r.score}</span><span className="text-[10px] text-stone-400 block">{r.band_pass ? "cutoff ✓" : "below cutoff"}</span></td>
                            <td className="py-2.5 pr-3 text-[11px] text-stone-600">{(r.sections || []).map((s) => `${s.label.split(" ")[0]} ${s.percent}%`).join(" · ")}</td>
                            <td className="py-2.5 pr-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${r.integrity === "clean" ? "bg-emerald-50 text-emerald-700" : r.integrity === "minor" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{r.integrity}</span></td>
                            <td className="py-2.5 text-[11px] text-stone-500">
                              {(r.violations || []).length === 0 ? "—" : (r.violations || []).map((v) => `${v.factor}×${v.count}`).join(", ")}
                              <span className="block text-stone-400">{r.snapshots} snapshots</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function CellBriefModal({ drive, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    cellCompanyBrief(drive.drive_id)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't build the company brief."));
  }, [drive.drive_id]);
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const setApproved = async (approved) => {
    setBusy(true);
    try { await cellApproveBrief(drive.drive_id, approved); setData((d) => ({ ...d, approved })); onChanged?.(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed to update."); }
    finally { setBusy(false); }
  };
  const Section = ({ title, items }) => !items?.length ? null : (
    <div className="mt-4">
      <p className="text-xs font-mono uppercase tracking-widest text-stone-400">{title}</p>
      <ul className="mt-1.5 space-y-1">
        {items.map((x, i) => <li key={i} className="text-sm text-stone-700 flex items-start gap-2"><span className="text-[#C2410C] mt-0.5">•</span> {x}</li>)}
      </ul>
    </div>
  );
  const intel = data?.intel;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[88vh] flex flex-col" data-testid="cell-brief-modal">
        <div className="flex items-start justify-between px-6 py-4 border-b border-stone-200 shrink-0 gap-3">
          <div>
            <h2 className="font-display text-2xl">About {drive.company_name} — review</h2>
            <p className="text-xs text-stone-500">Compiled from public sources. Students see this on the drive ONLY after you approve it.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100 shrink-0"><X size={18} /></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto">
          {err ? <p className="text-sm text-red-700">{err}</p> : !intel ? (
            <p className="text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Researching {drive.company_name} — first time takes ~30 seconds…</p>
          ) : (
            <>
              <p className="text-sm text-stone-700 leading-relaxed">{intel.overview}</p>
              <Section title="What they do" items={intel.what_they_do} />
              <Section title="Their fresher hiring process" items={intel.hiring_process} />
              <Section title="Interview tips from past candidates" items={intel.interview_tips} />
              <Section title="Good to know" items={intel.good_to_know} />
              {intel.sources?.length > 0 && (
                <p className="mt-4 text-[11px] text-stone-400 break-all">Sources: {intel.sources.slice(0, 4).join("  ·  ")}</p>
              )}
            </>
          )}
        </div>
        {intel && (
          <div className="px-6 py-4 border-t border-stone-200 shrink-0 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-stone-500">{data.approved ? "Published — visible on the drive card for students." : "Not published — students can't see this yet."}</p>
            {data.approved ? (
              <button onClick={() => setApproved(false)} disabled={busy} data-testid="brief-unapprove"
                className="text-xs font-medium px-4 py-2 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 disabled:opacity-60">
                {busy ? <Loader2 size={13} className="animate-spin inline" /> : "Unpublish"}
              </button>
            ) : (
              <button onClick={() => setApproved(true)} disabled={busy} data-testid="brief-approve"
                className="text-xs font-semibold px-4 py-2 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60">
                {busy ? <Loader2 size={13} className="animate-spin inline" /> : "Approve & publish to students"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RecruiterLinkButton({ drive }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const share = async () => {
    setBusy(true);
    try {
      let token = drive.share_token;
      if (!token) { const { data } = await shareDrive(drive.drive_id); token = data.share_token; }
      const url = `${window.location.origin}/d/${token}`;
      await navigator.clipboard.writeText(url);
      setCopied(true); setTimeout(() => setCopied(false), 2000);
    } catch (_) {} finally { setBusy(false); }
  };
  return (
    <button onClick={share} disabled={busy} data-testid={`cm-share-${drive.drive_id}`}
      className={`mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-full border transition-colors ${copied ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-stone-300 text-stone-700 hover:border-stone-900"}`}>
      {busy ? <Loader2 size={13} className="animate-spin" /> : copied ? <><Check size={13} /> Recruiter link copied</> : <><Link2 size={13} /> Copy recruiter shortlist link</>}
    </button>
  );
}

function DriveModal({ onClose, onSaved }) {
  const [f, setF] = useState({ title: "", company_name: "", company_about: "", role: "", ctc: "", ctc_lpa: "", deadline: "", min_cgpa: "", min_wishire_score: "" });
  const [notify, setNotify] = useState(false);
  const [depts, setDepts] = useState([]);
  const [selDepts, setSelDepts] = useState([]); // eligible departments
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  useEffect(() => { listDepartments().then(({ data }) => setDepts((data.departments || []).map((d) => d.name))).catch(() => {}); }, []);
  const toggleDept = (d) => setSelDepts((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);
  const [customDept, setCustomDept] = useState("");
  const addCustomDept = () => {
    const name = customDept.trim();
    if (!name) return;
    if (!selDepts.some((d) => d.toLowerCase() === name.toLowerCase())) setSelDepts((s) => [...s, name]);
    setCustomDept("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.company_name.trim() || !f.role.trim()) { setErr("Title, company, and role are required."); return; }
    setBusy(true); setErr("");
    const eligibility = {};
    if (f.min_cgpa !== "") eligibility.min_cgpa = Number(f.min_cgpa);
    if (f.min_wishire_score !== "") eligibility.min_wishire_score = Number(f.min_wishire_score);
    if (selDepts.length) eligibility.branches = selDepts;
    try {
      await createDrive({
        title: f.title.trim(), company_name: f.company_name.trim(),
        company_about: f.company_about.trim() || undefined,
        role: f.role.trim(), ctc: f.ctc.trim() || undefined,
        ctc_lpa: f.ctc_lpa !== "" ? Number(f.ctc_lpa) : undefined,
        deadline: f.deadline || undefined, eligibility,
        notify_students: notify,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not create drive."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-[#FDFBF7]">
          <h2 className="font-display text-2xl">New campus drive</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <Field label="Drive title *"><input value={f.title} onChange={set("title")} data-testid="cm-drive-title" placeholder="Summer 2026 SDE Hiring" className="cm-inp" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Company *"><input value={f.company_name} onChange={set("company_name")} data-testid="cm-drive-company" placeholder="Acme Corp" className="cm-inp" /></Field>
            <Field label="Role *"><input value={f.role} onChange={set("role")} placeholder="Backend Engineer" className="cm-inp" /></Field>
          </div>
          <Field label="About the company"><textarea value={f.company_about} onChange={set("company_about")} data-testid="cm-drive-about" rows={2} placeholder="A line or two about the company — students see this on the drive." className="cm-inp" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="CTC / Package"><input value={f.ctc} onChange={set("ctc")} placeholder="₹12 LPA" className="cm-inp" /></Field>
            <Field label="Package (LPA)"><input type="number" step="0.1" min="0" value={f.ctc_lpa} onChange={set("ctc_lpa")} data-testid="cm-drive-lpa" placeholder="12" className="cm-inp font-mono" /></Field>
            <Field label="Apply-by deadline"><input type="date" value={f.deadline} onChange={set("deadline")} data-testid="cm-drive-deadline" className="cm-inp" /></Field>
          </div>
          <p className="-mt-2 text-xs text-stone-400">Package (LPA) as a number powers your placement analytics — highest / median package in Reports.</p>
          <div className="pt-2 border-t border-stone-200">
            <p className="overline mb-3">Eligibility</p>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Min CGPA"><input type="number" step="0.1" min="0" max="10" value={f.min_cgpa} onChange={set("min_cgpa")} data-testid="cm-drive-cgpa" placeholder="7.0" className="cm-inp font-mono" /></Field>
              <Field label="Min Wishire Score"><input type="number" min="0" max="100" value={f.min_wishire_score} onChange={set("min_wishire_score")} data-testid="cm-drive-score" placeholder="60" className="cm-inp font-mono" /></Field>
            </div>
            <div className="mt-3">
              <ForecastPreview companyName={f.company_name} eligibility={{
                ...(f.min_cgpa !== "" ? { min_cgpa: Number(f.min_cgpa) } : {}),
                ...(f.min_wishire_score !== "" ? { min_wishire_score: Number(f.min_wishire_score) } : {}),
                ...(selDepts.length ? { branches: selDepts } : {}),
              }} />
            </div>
            <div className="mt-3">
              <label className="overline">Eligible departments {selDepts.length > 0 && <span className="text-stone-400">· {selDepts.length} selected</span>}</label>
              {/* Selected departments as removable chips */}
              {selDepts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="cm-drive-depts">
                  {selDepts.map((d) => (
                    <span key={d} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-stone-900 text-white">
                      {d}
                      <button type="button" onClick={() => toggleDept(d)} className="hover:text-red-300" aria-label={`Remove ${d}`}><X size={13} /></button>
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-2 grid grid-cols-2 gap-2">
                {/* Dropdown of the college's departments not yet selected */}
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) toggleDept(e.target.value); }}
                  data-testid="cm-drive-dept-select"
                  className="cm-inp"
                >
                  <option value="">{depts.length ? "Add department…" : "No departments defined yet"}</option>
                  {depts.filter((d) => !selDepts.includes(d)).map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
                {/* Custom department for one-off drives */}
                <div className="flex gap-2">
                  <input
                    value={customDept}
                    onChange={(e) => setCustomDept(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomDept(); } }}
                    data-testid="cm-drive-dept-custom"
                    placeholder="Custom (e.g. M.Tech AI)"
                    className="cm-inp flex-1"
                  />
                  <button type="button" onClick={addCustomDept} data-testid="cm-drive-dept-add"
                    className="px-3 rounded-xl border border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors" aria-label="Add custom department">
                    <Plus size={16} />
                  </button>
                </div>
              </div>
              <p className="mt-1.5 text-xs text-stone-400">No departments selected = open to all.</p>
            </div>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-stone-700 cursor-pointer">
            <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="cm-drive-notify" className="w-4 h-4 accent-[#C2410C]" />
            <span className="inline-flex items-center gap-1.5"><Mail size={14} className="text-stone-400" /> Email eligible students about this drive</span>
          </label>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="submit" disabled={busy} data-testid="cm-drive-submit" className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <>Post drive <Megaphone size={15} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

function ApplicantsModal({ drive, onClose }) {
  const [rows, setRows] = useState(null);
  const load = () => driveApplicants(drive.drive_id).then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const move = async (a, status) => {
    setRows((arr) => arr.map((x) => (x.app_id === a.app_id ? { ...x, status } : x)));
    try { await updateApplication(a.app_id, status); } catch (_) { load(); }
  };
  const [exporting, setExporting] = useState(false);
  const shortlisted = (rows || []).filter((a) => ["shortlisted", "interview", "offer", "placed"].includes(a.status)).length;
  const exportReports = async () => {
    setExporting(true);
    try {
      const { data } = await driveReports(drive.drive_id);
      if (!data.count) { toast.error("No applicants to export yet."); return; }
      if (!openCandidateReports(data.reports, `${drive.company_name} · ${drive.title} · ${data.count} candidate${data.count === 1 ? "" : "s"}`)) toast.error("Allow pop-ups to open the reports.");
    } catch (_) { toast.error("Could not build the reports."); }
    finally { setExporting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div><h2 className="font-display text-2xl">{drive.title}</h2><p className="text-sm text-stone-500">{drive.company_name} · applicants ranked by score</p></div>
          <div className="flex items-center gap-2">
            {(rows || []).length > 0 && (
              <button onClick={exportReports} disabled={exporting} data-testid="drive-export-reports" title={shortlisted ? `One printable report per shortlisted candidate (${shortlisted})` : "One printable report per applicant"}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900">
                {exporting ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} {shortlisted ? `Reports for ${shortlisted} shortlisted` : "Recruiter reports"}
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
          </div>
        </div>
        <div className="overflow-y-auto">
          {rows === null ? <p className="p-8 text-stone-500">Loading…</p> : rows.length === 0 ? (
            <p className="p-8 text-center text-stone-500">No applicants yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
                {["Student", "Branch", "CGPA", "Score", "Status"].map((h) => <th key={h} className="text-left px-5 py-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.app_id} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="px-5 py-3"><p className="font-medium">{a.student_name}</p><p className="text-xs text-stone-500">{a.student_email}</p></td>
                    <td className="px-5 py-3 text-stone-600">{a.branch || "—"}</td>
                    <td className="px-5 py-3 text-stone-600">{a.cgpa ?? "—"}{a.cgpa_verified && <ShieldCheck size={13} className="inline ml-1 text-emerald-600" />}</td>
                    <td className="px-5 py-3"><ScorePill score={a.wishire_score} /></td>
                    <td className="px-5 py-3">
                      <select value={a.status} onChange={(e) => move(a, e.target.value)} className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer font-semibold ${STAGE_STYLE[a.status] || STAGE_STYLE.applied}`}>
                        {APP_STAGES.map((st) => <option key={st} value={st}>{st}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────── Departments ─────────── */
function DepartmentsModal({ onClose }) {
  const [rows, setRows] = useState(null);        // [{name, students}]
  const [unassigned, setUnassigned] = useState(0);
  const [draft, setDraft] = useState([]);        // editable name list
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => listDepartments().then(({ data }) => {
    setRows(data.departments); setUnassigned(data.unassigned || 0);
    setDraft(data.departments.map((d) => d.name));
  }).catch(() => setRows([]));
  useEffect(() => { load(); }, []); // eslint-disable-line

  const add = () => {
    const v = adding.trim();
    if (v && !draft.some((d) => d.toLowerCase() === v.toLowerCase())) setDraft((x) => [...x, v]);
    setAdding("");
  };
  const remove = (i) => setDraft((x) => x.filter((_, j) => j !== i));

  const save = async () => {
    setBusy(true); setMsg(null);
    try { await setDepartments(draft); setMsg({ ok: true, text: "Departments saved." }); load(); }
    catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || "Could not save (owner only)." }); }
    finally { setBusy(false); }
  };

  const countOf = (name) => (rows || []).find((r) => r.name.toLowerCase() === name.toLowerCase())?.students ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-[#FDFBF7]">
          <div><h2 className="font-display text-2xl">Departments</h2><p className="text-xs text-stone-500">Students pick from this list; drives target these.</p></div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <div className="px-6 py-5">
          <div className="flex gap-2">
            <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
              data-testid="cm-dept-add-input" placeholder="e.g. CSE, ECE, Mechanical…" className="cm-inp flex-1" />
            <button type="button" onClick={add} data-testid="cm-dept-add" className="btn-ghost"><Plus size={15} /> Add</button>
          </div>
          {draft.length === 0 ? (
            <p className="mt-5 text-sm text-stone-500">No departments yet. Add your branches (CSE, ECE, …) so students select the right one and drives can target them.</p>
          ) : (
            <div className="mt-5 space-y-2">
              {draft.map((d, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-2.5">
                  <span className="font-medium text-stone-800 flex items-center gap-2"><Layers size={14} className="text-stone-400" /> {d}</span>
                  <span className="flex items-center gap-3">
                    <span className="text-xs text-stone-400 font-mono">{countOf(d)} student{countOf(d) === 1 ? "" : "s"}</span>
                    <button onClick={() => remove(i)} className="p-1 rounded text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
          {unassigned > 0 && <p className="mt-3 text-xs text-amber-700">{unassigned} student(s) have a branch not in this list — they'll need to re-select their department.</p>}
          {msg && <p className={`mt-3 text-sm ${msg.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="cm-dept-msg">{msg.text}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button onClick={onClose} className="btn-ghost">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="cm-dept-save" className="btn-primary disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : "Save departments"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─────────── Bulk import ─────────── */
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

// Tolerant CSV parser for student imports. Handles comma / semicolon / tab
// delimiters (Excel's regional CSV exports use semicolons), quoted cells,
// missing names, and junk lines. Returns {rows, skipped, binary}.
function parseCsv(text) {
  // Excel .xlsx pasted/uploaded as-is comes through as binary (ZIP starts "PK").
  if (text.startsWith("PK") || /[\u0000-\u0008]/.test(text)) {
    return { rows: [], skipped: 0, binary: true };
  }
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (!lines.length) return { rows: [], skipped: 0, binary: false };

  // Pick the delimiter that appears most in the first data line.
  const sample = lines[0];
  const delim = [";", "\t", ","].reduce((best, d) =>
    sample.split(d).length > sample.split(best).length ? d : best, ",");
  const split = (line) => line.split(delim).map((x) => x.trim().replace(/^"(.*)"$/, "$1"));

  const header = lines[0].toLowerCase();
  const hasHeader = header.includes("email") || (header.includes("name") && !EMAIL_RE.test(split(lines[0])[1] || ""));
  const body = hasHeader ? lines.slice(1) : lines;
  const idx = hasHeader
    ? (() => {
        const cols = split(lines[0]).map((c) => c.toLowerCase());
        return { name: cols.indexOf("name"), email: cols.indexOf("email"), branch: cols.findIndex((c) => c === "branch" || c === "department" || c === "dept"), section: cols.findIndex((c) => c === "section" || c === "sec"), cgpa: cols.indexOf("cgpa"), grad_year: cols.findIndex((c) => c.includes("grad") || c.includes("year")), phone: cols.findIndex((c) => c.includes("phone") || c.includes("mobile")), roll_no: cols.findIndex((c) => c.includes("roll") || c.includes("enrol")), city: cols.indexOf("city") };
      })()
    : { name: 0, email: 1, branch: 2, section: -1, cgpa: 3, grad_year: 4, phone: -1, roll_no: -1, city: -1 };

  let skipped = 0;
  const rows = [];
  for (const line of body) {
    const c = split(line);
    const get = (i) => (i >= 0 && i < c.length ? c[i] : "");
    let email = get(idx.email);
    let name = get(idx.name);
    // No header + columns swapped, or email-only lines: find the email anywhere.
    if (!EMAIL_RE.test(email)) {
      const found = c.find((x) => EMAIL_RE.test(x));
      if (found) email = found;
    }
    if (!EMAIL_RE.test(email)) { skipped += 1; continue; }
    name = name.replace(/^["']+|["']+$/g, "").trim();
    if (!name || EMAIL_RE.test(name)) name = email.split("@")[0].replace(/[._-]+/g, " ").trim();
    const row = { name, email };
    if (get(idx.branch) && !EMAIL_RE.test(get(idx.branch))) row.branch = get(idx.branch);
    if (idx.section >= 0 && get(idx.section) && !EMAIL_RE.test(get(idx.section))) row.section = get(idx.section);
    const cg = Number(get(idx.cgpa));
    if (get(idx.cgpa) && Number.isFinite(cg) && cg >= 0 && cg <= 10) row.cgpa = cg;
    const gy = Number(get(idx.grad_year));
    if (get(idx.grad_year) && Number.isInteger(gy) && gy >= 1990 && gy <= 2100) row.grad_year = gy;
    if (idx.phone >= 0 && get(idx.phone)) row.phone = get(idx.phone);
    if (idx.roll_no >= 0 && get(idx.roll_no)) row.roll_no = get(idx.roll_no);
    if (idx.city >= 0 && get(idx.city) && !EMAIL_RE.test(get(idx.city))) row.city = get(idx.city);
    rows.push(row);
  }
  return { rows, skipped, binary: false };
}

function BuySeatsModal({ seats, onClose }) {
  const [n, setN] = useState(50);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await requestLicenses(Number(n), note); setSent(true); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Could not send the request."); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#FDFBF7] rounded-3xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <h2 className="font-display text-2xl">Buy more licenses</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        {sent ? (
          <div className="px-6 py-8 text-center">
            <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={22} /></div>
            <p className="font-display text-2xl">Request sent</p>
            <p className="text-sm text-stone-600 mt-2 max-w-xs mx-auto">Your Wishire account manager will confirm the additional {n} seats and activate them for you.</p>
            <button onClick={onClose} className="btn-primary mt-5">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <p className="text-sm text-stone-600">Using <strong>{seats?.active ?? "?"}</strong> of <strong>{seats?.licensed ?? "?"}</strong> licensed seats{seats?.locked ? <> &mdash; <strong className="text-red-700">{seats.locked} student{seats.locked === 1 ? "" : "s"} locked</strong> and waiting for a seat</> : null}.</p>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-stone-400">Additional seats</span>
              <input type="number" min="1" max="100000" value={n} onChange={(e) => setN(e.target.value)} data-testid="buy-seats-n" className="cm-inp mt-1.5" />
            </label>
            <label className="block">
              <span className="text-xs font-mono uppercase tracking-widest text-stone-400">Note (optional)</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="New 2027 batch onboarding" className="cm-inp mt-1.5" />
            </label>
            {err && <p className="text-sm text-red-700">{err}</p>}
            <button type="submit" disabled={busy} data-testid="buy-seats-submit" className="btn-primary w-full justify-center disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Request licenses"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function ImportModal({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    if (/\.(xlsx|xls)$/i.test(f.name)) {
      setErr("That's an Excel file — open it in Excel and use File → Save As → CSV, then upload the .csv.");
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const submit = async () => {
    const { rows, skipped, binary } = parseCsv(text);
    if (binary) { setErr("This looks like an Excel/binary file, not CSV text. In Excel: File → Save As → 'CSV (Comma delimited)', then upload that file."); return; }
    if (!rows.length) {
      setErr(skipped > 0
        ? `Found ${skipped} line${skipped === 1 ? "" : "s"} but none had a valid email address. Each row needs an email like name@college.edu.`
        : "No rows found. Paste CSV lines or upload a .csv file — one student per line: name, email, branch, cgpa, grad year.");
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await importStudents(rows);
      setResult({ ...data, parse_skipped: skipped });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Import failed — check that every row has a name and a valid email, then try again.");
    }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-[#FDFBF7]">
          <h2 className="font-display text-2xl">Import students</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        {result ? (
          <div className="px-6 py-6 text-center">
            <div className="inline-flex p-3 rounded-2xl bg-emerald-100 text-emerald-700 mb-3"><CheckCircle2 size={22} /></div>
            <p className="font-display text-2xl">Batch imported</p>
            <p className="text-sm text-stone-600 mt-2"><strong>{result.created}</strong> created · <strong>{result.linked}</strong> linked · <strong>{result.skipped}</strong> skipped{result.parse_skipped > 0 ? ` · ${result.parse_skipped} line${result.parse_skipped === 1 ? "" : "s"} ignored (no valid email)` : ""}</p>
            {result.locked > 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mt-3">
                {result.activated} student{result.activated === 1 ? "" : "s"} got a licensed seat — <strong>{result.locked} imported locked</strong> (all {result.seats?.licensed} licenses in use). They appear in your list with a lock and can't use Wishire until you buy more licenses.
              </p>
            )}
            {result.errors?.length > 0 && (
              <div className="mt-4 text-left text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl p-3 max-h-32 overflow-y-auto">
                {result.errors.map((e, i) => <div key={i}>{e.email}: {e.reason}</div>)}
              </div>
            )}
            <p className="text-xs text-stone-500 mt-4">New students set their own password via “Forgot password” on the login page.</p>
            <button onClick={onDone} className="btn-primary mt-5">Done</button>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <p className="text-sm text-stone-600">Paste CSV rows or upload a <code>.csv</code> file. Columns: <span className="font-mono text-xs">name, email, branch, section, cgpa, grad_year, phone, roll_no, city</span> — only name + email are required (header row recommended).</p>
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-stone-300 text-sm cursor-pointer hover:bg-stone-100 w-fit">
              <Upload size={14} /> Choose CSV file
              <input type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="cm-import-file" />
            </label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} data-testid="cm-import-text"
              placeholder={"name,email,branch,cgpa,grad_year\nAsha R,asha@college.edu,CSE,8.4,2026\nVikram S,vik@college.edu,ECE,7.1,2026"}
              className="cm-inp font-mono text-xs leading-relaxed" />
            {err && <p className="text-sm text-red-700">{err}</p>}
            <button onClick={submit} disabled={busy} data-testid="cm-import-submit" className="btn-primary w-full justify-center disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <>Import batch <Upload size={15} /></>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────── Announcements ─────────── */
export function CampusAnnouncements() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", notify_email: false });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => listAnnouncements().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const post = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) { setMsg("Title and message are required."); return; }
    setBusy(true); setMsg(null);
    try {
      const { data } = await createAnnouncement(form);
      setMsg(form.notify_email ? `Posted and emailed to ${data.emailed} students.` : "Posted to the student feed.");
      setForm({ title: "", body: "", notify_email: false });
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not post."); }
    finally { setBusy(false); }
  };

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Announcements</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Broadcast to your batch.</h1>
      <p className="text-stone-500 mt-2 max-w-lg">Post drive updates, deadlines, and reminders — students see them on their Campus page. Optionally email everyone too.</p>

      <form onSubmit={post} className="mt-8 rounded-3xl border border-stone-200 bg-white p-6 space-y-3 max-w-2xl">
        <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="cm-ann-title" placeholder="Title (e.g. Google drive — apply by Friday)" className="cm-inp" />
        <textarea value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="cm-ann-body" rows={4} placeholder="Message to your students…" className="cm-inp" />
        <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
          <input type="checkbox" checked={form.notify_email} onChange={(e) => setForm((f) => ({ ...f, notify_email: e.target.checked }))} data-testid="cm-ann-email" className="w-4 h-4 accent-[#C2410C]" />
          Also email every student
        </label>
        {msg && <p className="text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2">{msg}</p>}
        <button type="submit" disabled={busy} data-testid="cm-ann-submit" className="btn-primary disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <><Megaphone size={15} /> Post announcement</>}
        </button>
      </form>

      <div className="mt-10 space-y-3 max-w-2xl">
        {rows === null ? <p className="text-stone-500">Loading…</p> : rows.length === 0 ? (
          <p className="text-stone-400 text-sm">No announcements yet.</p>
        ) : rows.map((a) => (
          <div key={a.id} className="rounded-2xl border border-stone-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <p className="font-display text-lg">{a.title}</p>
              <span className="text-xs text-stone-400 shrink-0">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</span>
            </div>
            <p className="mt-1.5 text-sm text-stone-600 whitespace-pre-wrap">{a.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Reports ─────────── */
// NIRF-style annual placement report — opens print-ready in a new window so the
// TPO can save it as PDF for accreditation / management submissions.
function openPrintReport(data) {
  const esc = (s) => String(s ?? "—").replace(/</g, "&lt;");
  const fmtLpa = (v) => (v == null ? "—" : `₹${v} LPA`);
  const ay = data.academic_year || String(new Date().getFullYear());
  const genDate = new Date(data.generated_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const rows = (arr, cells) => (arr || []).map((r) => `<tr>${cells(r).map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("");
  const t = data.training || {};
  const base = data.placeable_base ?? data.total_students;
  const html = `<!doctype html><html><head><title>Annual Placement Report ${ay} — ${esc(data.college_name)}</title>
  <style>
    body{font-family:Georgia,'Times New Roman',serif;color:#1c1917;margin:44px 48px;line-height:1.5;font-size:13px}
    h1{font-size:25px;margin:0} h2{font-size:15px;margin:26px 0 6px;border-bottom:2px solid #1c1917;padding-bottom:4px}
    h2 .no{color:#78716c;font-weight:normal;margin-right:8px}
    .sub{color:#57534e;font-size:12.5px;margin-top:4px}
    table{width:100%;border-collapse:collapse;margin-top:8px;font-size:12.5px}
    th,td{border:1px solid #a8a29e;padding:6px 10px;text-align:left;vertical-align:top}
    th{background:#f5f5f4;font-size:10.5px;text-transform:uppercase;letter-spacing:.06em}
    .kpis{display:flex;gap:0;margin-top:18px;border:1px solid #a8a29e}
    .kpi{flex:1;padding:12px 14px;border-right:1px solid #a8a29e}.kpi:last-child{border-right:0}
    .kpi b{display:block;font-size:21px}.kpi span{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#57534e}
    .head{display:flex;align-items:center;gap:18px;border-bottom:3px double #1c1917;padding-bottom:14px}
    .head img{width:70px;height:70px;object-fit:contain}
    .meta{display:flex;justify-content:space-between;font-size:11px;color:#57534e;margin-top:8px}
    .decl{margin-top:26px;border:1px solid #a8a29e;padding:12px 16px;font-size:12px}
    .sigs{display:flex;justify-content:space-between;gap:40px;margin-top:52px}
    .sig{flex:1;text-align:center}
    .sig .line{border-top:1px solid #1c1917;margin-top:44px;padding-top:6px;font-size:12px}
    .sig .role{font-size:10.5px;text-transform:uppercase;letter-spacing:.08em;color:#57534e}
    .foot{margin-top:30px;font-size:10.5px;color:#78716c;border-top:1px solid #d6d3d1;padding-top:8px}
    .note{font-size:11px;color:#57534e;margin-top:4px}
    @media print{body{margin:20px 26px}}
  </style></head><body>

  <div class="head">
    ${data.college_logo ? `<img src="${data.college_logo}" alt="logo" />` : ""}
    <div style="flex:1">
      <h1>${esc(data.college_name)}</h1>
      <p class="sub"><b>Annual Placement Report — Academic Year ${esc(ay)}</b><br/>
      For submission to NIRF · NAAC · AICTE / affiliating university, and management review</p>
    </div>
  </div>
  <div class="meta">
    <span>Report ID: <b>${esc(data.report_id || "—")}</b></span>
    <span>Generated: ${genDate}</span>
    <span>Source: Institution placement records (system-generated)</span>
  </div>

  <h2><span class="no">1.</span>Executive summary</h2>
  <div class="kpis">
    <div class="kpi"><b>${data.total_students}</b><span>Final-year students</span></div>
    <div class="kpi"><b>${base}</b><span>Placeable base</span></div>
    <div class="kpi"><b>${data.total_placed}</b><span>Placed</span></div>
    <div class="kpi"><b>${data.placement_pct}%</b><span>Placement rate</span></div>
    <div class="kpi"><b>${fmtLpa(data.packages?.median_lpa)}</b><span>Median package</span></div>
    <div class="kpi"><b>${fmtLpa(data.packages?.highest_lpa)}</b><span>Highest package</span></div>
  </div>
  <p class="note">Placement rate is computed on the placeable base of ${base}: ${data.higher_studies || 0} student(s) progressed to higher studies and ${data.opted_out || 0} opted out of placements — excluded per NIRF methodology. Median salary is the NIRF-preferred central measure.</p>

  <h2><span class="no">2.</span>Placement outcomes — three-year statement (NIRF format)</h2>
  <table><tr><th>Academic year</th><th>Placeable students</th><th>Students placed</th><th>Placement %</th><th>Median salary</th><th>Data source</th></tr>
  ${rows(data.yearly_trend, (y) => [y.year, y.placeable, y.placed, `${y.placement_pct}%`, fmtLpa(y.median_lpa), y.source === "wishire-live" ? "Current season (live records)" : "Institution records"])}</table>
  ${(data.yearly_trend || []).length < 2 ? `<p class="note">Prior-year rows appear automatically once the placement cell enters historical figures in Profile → Placement history.</p>` : ""}

  <h2><span class="no">3.</span>Department-wise placement</h2>
  <table><tr><th>Department</th><th>Students</th><th>Placeable</th><th>Placed</th><th>Placement %</th></tr>
  ${rows(data.by_branch, (b) => [b.branch, b.students, b.placeable ?? b.students, b.placed, `${b.placement_pct}%`])}</table>

  <h2><span class="no">4.</span>Salary distribution of offers</h2>
  <table><tr><th>CTC band</th><th>Offers</th></tr>
  ${rows(data.salary_bands, (s) => [s.band, s.offers])}
  <tr><td><b>Total offers with disclosed CTC</b></td><td><b>${data.packages?.offers_with_package ?? 0}</b></td></tr></table>
  <p class="note">Average package: ${fmtLpa(data.packages?.average_lpa)} · Median: ${fmtLpa(data.packages?.median_lpa)} · Highest: ${fmtLpa(data.packages?.highest_lpa)}</p>

  <h2><span class="no">5.</span>Participating recruiters</h2>
  <table><tr><th>Company</th><th>Drives</th><th>Applicants</th><th>Offers</th><th>Best package</th></tr>
  ${rows(data.by_company, (c) => [c.company, c.drives, c.applicants, c.offers, fmtLpa(c.best_lpa)])}</table>

  <h2><span class="no">6.</span>Selection funnel (all drives, ${esc(ay)})</h2>
  <table><tr><th>Applied</th><th>Shortlisted</th><th>Interview</th><th>Offers</th><th>Placed</th></tr>
  <tr><td>${data.funnel?.applied ?? 0}</td><td>${data.funnel?.shortlisted ?? 0}</td><td>${data.funnel?.interview ?? 0}</td><td>${data.funnel?.offer ?? 0}</td><td>${data.funnel?.placed ?? 0}</td></tr></table>

  <h2><span class="no">7.</span>Career guidance & training (NAAC Criterion 5 mapping)</h2>
  <table><tr><th>NAAC metric</th><th>Description</th><th>Value (${esc(ay)})</th></tr>
  <tr><td>5.1.2 / 5.1.3</td><td>Students who received career counselling &amp; capability-enhancement training (AI mock interviews, proctored assessments, personalised placement roadmaps)</td><td>${t.any_training ?? 0} students</td></tr>
  <tr><td>—</td><td>&nbsp;&nbsp;of which: AI mock-interview training</td><td>${t.interview_trained ?? 0}</td></tr>
  <tr><td>—</td><td>&nbsp;&nbsp;of which: proctored aptitude / company-pattern tests</td><td>${t.test_trained ?? 0}</td></tr>
  <tr><td>—</td><td>&nbsp;&nbsp;of which: active personalised placement roadmaps</td><td>${t.roadmap_active ?? 0}</td></tr>
  <tr><td>5.2.1</td><td>Placement of outgoing students (on placeable base)</td><td>${data.total_placed} of ${base} (${data.placement_pct}%)</td></tr>
  <tr><td>5.2.2</td><td>Students progressing to higher education</td><td>${data.higher_studies ?? 0}</td></tr></table>

  <div class="decl">
    <b>Declaration.</b> The figures in this report are generated from the institution's digital placement
    records — student registrations, drive applications, offers and training activity — for
    ${esc(data.college_name)}, academic year ${esc(ay)}. Placement percentages follow the NIRF placeable-base
    methodology. Historical rows marked “Institution records” were provided by the placement cell.
  </div>

  <div class="sigs">
    <div class="sig"><div class="line">&nbsp;</div><p class="role">Training &amp; Placement Officer</p></div>
    <div class="sig"><div class="line">&nbsp;</div><p class="role">Dean / Principal</p></div>
    <div class="sig"><div class="line">Date &nbsp;·&nbsp; Place</div><p class="role">Seal of the institution</p></div>
  </div>

  <p class="foot">Report ${esc(data.report_id || "")} · system-generated on ${genDate} from the institution's placement records.</p>
  <script>window.onload = () => window.print();</script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

export function CampusReports() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  useEffect(() => {
    campusReports().then(({ data }) => setData(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load reports."));
  }, []);

  const generateAiSummary = async () => {
    setAiBusy(true);
    try {
      const { data: r } = await copilotAsk(
        "Write a 4-5 sentence executive summary of our placement season for the principal/management: overall readiness, placement progress, standout numbers, and the single biggest risk. Plain prose, no bullet points, no greetings.");
      setAiSummary(r.answer);
    } catch (e) { setAiSummary(e?.response?.data?.detail || "Could not generate the summary — try again."); }
    finally { setAiBusy(false); }
  };

  const exportCsv = () => {
    if (!data) return;
    const lines = ["Branch,Students,Avg readiness,Job-ready,At risk,Placed,Placement %"];
    data.by_branch.forEach((b) => lines.push([b.branch, b.students, b.avg_readiness, b.job_ready, b.at_risk, b.placed, b.placement_pct].join(",")));
    lines.push("", "Company,Drives,Applicants,Offers,Best LPA");
    (data.by_company || []).forEach((c) => lines.push([c.company, c.drives, c.applicants, c.offers, c.best_lpa ?? ""].join(",")));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `placement-report-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (err) return <div className="container-x py-14"><div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 text-amber-800"><BarChart3 className="mb-3" /> {err}</div></div>;
  if (!data) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading reports…</div>;

  const fn = data.funnel || {};
  const pk = data.packages || {};

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div><span className="overline">Reports</span><h1 className="font-display text-4xl md:text-5xl mt-2">Placement analytics.</h1></div>
        <div className="flex flex-wrap gap-2 max-w-full">
          <button onClick={generateAiSummary} disabled={aiBusy} data-testid="cm-report-ai" className="btn-ghost disabled:opacity-60">
            {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} AI summary
          </button>
          <button onClick={() => openPrintReport(data)} data-testid="cm-report-print" className="btn-primary"><Printer size={16} /> Placement report (PDF)</button>
          <button onClick={exportCsv} data-testid="cm-report-export" className="btn-ghost"><Download size={16} /> CSV</button>
        </div>
      </div>
      {aiSummary && (
        <div className="mb-8 rounded-3xl border border-purple-200 bg-purple-50/60 p-6" data-testid="cm-report-ai-summary">
          <span className="overline flex items-center gap-1.5 text-purple-700"><Sparkles size={13} /> Executive summary · AI-generated</span>
          <p className="mt-2 text-stone-800 leading-relaxed">{aiSummary}</p>
          <p className="mt-2 text-xs text-stone-400">Grounded in your live season data — paste it straight into your management update.</p>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Stat icon={Users} label={`Placeable base${data.placeable_base != null && data.placeable_base !== data.total_students ? ` (of ${data.total_students})` : ""}`} value={data.placeable_base ?? data.total_students} to="#" accent="#0a66c2" />
        <Stat icon={Award} label="Avg readiness" value={data.avg_readiness} to="#" accent="#7C3AED" />
        <Stat icon={CheckCircle2} label="Placed" value={data.total_placed} to="#" accent="#15803D" />
        <Stat icon={BarChart3} label="Placement %" value={`${data.placement_pct}%`} to="#" accent="#C2410C" />
      </div>
      {(data.higher_studies > 0 || data.opted_out > 0) && (
        <p className="text-xs text-stone-500 -mt-2 mb-4">Placement % is on the placeable base — {[data.higher_studies && `${data.higher_studies} higher studies`, data.opted_out && `${data.opted_out} opted out`].filter(Boolean).join(", ")} excluded (NIRF-aligned).</p>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={Award} label="Highest package" value={pk.highest_lpa != null ? `₹${pk.highest_lpa}L` : "—"} to="#" accent="#15803D" />
        <Stat icon={Award} label="Median package" value={pk.median_lpa != null ? `₹${pk.median_lpa}L` : "—"} to="#" accent="#0a66c2" />
        <Stat icon={AlertTriangle} label="Still unplaced" value={data.unplaced_count ?? "—"} to="/campus/students" accent="#D97706" />
        <Stat icon={Building2} label="Companies" value={(data.by_company || []).length} to="/campus/recruiters" accent="#57534E" />
      </div>

      {/* Selection funnel */}
      <h2 className="font-display text-2xl mb-3">Selection funnel</h2>
      <div className="mb-10 grid grid-cols-5 rounded-2xl border border-stone-200 bg-white overflow-hidden text-center" data-testid="cm-funnel">
        {[["Applied", fn.applied], ["Shortlisted", fn.shortlisted], ["Interview", fn.interview], ["Offers", fn.offer], ["Placed", fn.placed]].map(([label, v], i) => (
          <div key={label} className={`py-5 ${i > 0 ? "border-l border-stone-100" : ""}`}>
            <p className="font-display text-3xl">{v ?? 0}</p>
            <p className="overline mt-1">{label}</p>
          </div>
        ))}
      </div>
      <h2 className="font-display text-2xl mb-3">By branch</h2>
      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
            {["Branch", "Students", "Avg readiness", "Job-ready", "At risk", "Placed", "Placement %"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody>
            {data.by_branch.map((b) => (
              <tr key={b.branch} className="border-b border-stone-50 hover:bg-stone-50">
                <td className="px-4 py-3 font-medium">{b.branch}</td>
                <td className="px-4 py-3">{b.students}</td>
                <td className="px-4 py-3 font-mono">{b.avg_readiness}</td>
                <td className="px-4 py-3 text-emerald-700">{b.job_ready}</td>
                <td className="px-4 py-3 text-red-600">{b.at_risk}</td>
                <td className="px-4 py-3">{b.placed}</td>
                <td className="px-4 py-3 font-mono">{b.placement_pct}%</td>
              </tr>
            ))}
            {data.by_branch.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-stone-400">No students yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {(data.by_company || []).length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-3 mt-10">By company</h2>
          <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            <table className="w-full text-sm" data-testid="cm-company-table">
              <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
                {["Company", "Drives", "Applicants", "Offers", "Best package"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.by_company.map((c) => (
                  <tr key={c.company} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium">{c.company}</td>
                    <td className="px-4 py-3">{c.drives}</td>
                    <td className="px-4 py-3">{c.applicants}</td>
                    <td className="px-4 py-3 text-emerald-700">{c.offers}</td>
                    <td className="px-4 py-3 font-mono">{c.best_lpa != null ? `₹${c.best_lpa}L` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {data.drives.length > 0 && (
        <>
          <h2 className="font-display text-2xl mb-3 mt-10">By drive</h2>
          <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
                {["Drive", "Company", "Applicants", "Shortlisted", "Placed"].map((h) => <th key={h} className="text-left px-4 py-3 font-normal">{h}</th>)}
              </tr></thead>
              <tbody>
                {data.drives.map((d, i) => (
                  <tr key={i} className="border-b border-stone-50 hover:bg-stone-50">
                    <td className="px-4 py-3 font-medium">{d.title}</td>
                    <td className="px-4 py-3 text-stone-600">{d.company_name}</td>
                    <td className="px-4 py-3">{d.applicants}</td>
                    <td className="px-4 py-3">{d.shortlisted}</td>
                    <td className="px-4 py-3 text-emerald-700">{d.placed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────── Recruiter CRM ─────────── */
const REC_STATUS = {
  prospect: { cls: "bg-stone-100 text-stone-600", label: "Prospect" },
  contacted: { cls: "bg-blue-100 text-blue-700", label: "Contacted" },
  visiting: { cls: "bg-purple-100 text-purple-700", label: "Visiting" },
  active: { cls: "bg-emerald-100 text-emerald-700", label: "Active" },
  dormant: { cls: "bg-amber-100 text-amber-700", label: "Dormant" },
};

const SOURCE_STYLE = {
  Naukri: "bg-blue-100 text-blue-700",
  Internshala: "bg-cyan-100 text-cyan-700",
  Freshersworld: "bg-purple-100 text-purple-700",
  LinkedIn: "bg-sky-100 text-sky-700",
  Indeed: "bg-indigo-100 text-indigo-700",
  "Google Jobs": "bg-emerald-100 text-emerald-700",
  Web: "bg-stone-100 text-stone-600",
};

// Role/department → canonical buckets, so "CSE" matches "Fresher Software
// Engineer" and "Mechanical" matches "Graduate Design Engineer".
const DEPT_BUCKET_RULES = [
  { bucket: "software", re: /\b(software|sde|developer|full[- ]?stack|backend|frontend|web|app|java|python|qa|test|data (analyst|scien|engineer)|ai|ml|machine learning|devops|cloud|it|computer|cse|mca|bca|information tech)\b/i },
  { bucket: "electronics", re: /\b(electronic|electrical|embedded|vlsi|hardware|telecom|iot|circuit|semiconductor|ece|eee|instrumentation)\b/i },
  { bucket: "mechanical", re: /\b(mechanical|production|manufactur|cad|cae|design engineer|automobile|automotive|hvac|thermal|mech)\b/i },
  { bucket: "civil", re: /\b(civil|structural|site engineer|construction|surveyor|planning engineer)\b/i },
  { bucket: "business", re: /\b(business analyst|marketing|sales|hr\b|human resource|operations|finance|financ|account|audit|banking|mba|bba|b\.?com|commerce|management|consult)\b/i },
];
const textBuckets = (text) => {
  const hits = DEPT_BUCKET_RULES.filter((r) => r.re.test(text || "")).map((r) => r.bucket);
  return hits.length ? hits : ["general"];
};

/* One HR/CXO contact with per-person actions: the college picks exactly WHO
   to email (Get email resolves via their LinkedIn URL) and gets a mail
   personalized to that person (Draft). */
function ContactChip({ lead, contact, onUpdated, onDraft }) {
  const [busy, setBusy] = useState(null); // "email" | "draft"
  const [note, setNote] = useState(null);

  const getEmail = async () => {
    setBusy("email"); setNote(null);
    try {
      const { data } = await contactEmail(lead.lead_id, contact.linkedin_url);
      onUpdated?.(data.contact);
    } catch (e) { setNote(e?.response?.data?.detail || "No email found."); }
    finally { setBusy(null); }
  };

  const draft = async () => {
    setBusy("draft"); setNote(null);
    try {
      const { data } = await draftLeadOutreach(lead.lead_id, contact.linkedin_url);
      onDraft?.(data, contact);
    } catch (e) { setNote(e?.response?.data?.detail || "Could not draft."); }
    finally { setBusy(null); }
  };

  return (
    <span className={`inline-flex flex-wrap items-center gap-1.5 pl-3 pr-1.5 py-1.5 rounded-2xl border text-xs max-w-full ${contact.email ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-sky-50 border-sky-200 text-sky-800"}`}
      data-testid={`contact-${(contact.name || "").replace(/\s+/g, "-").toLowerCase()}`}>
      {contact.email ? <Mail size={11} /> : <Linkedin size={11} />}
      {contact.linkedin_url ? (
        <a href={contact.linkedin_url.startsWith("http") ? contact.linkedin_url : `https://${contact.linkedin_url}`} target="_blank" rel="noreferrer" className="font-medium hover:underline">{contact.name || "Profile"}</a>
      ) : (
        <span className="font-medium">{contact.name || contact.email}</span>
      )}
      {contact.title && <span className={`truncate max-w-[150px] ${contact.email ? "text-emerald-600" : "text-sky-600"}`}>· {contact.title}</span>}
      {contact.email ? (
        <a href={`mailto:${contact.email}`} className="font-mono underline decoration-emerald-300 hover:decoration-emerald-700 break-all">{contact.email}</a>
      ) : (
        contact.linkedin_url && (
          <button onClick={getEmail} disabled={!!busy} title="Find this person's email via their LinkedIn profile"
            className="px-2 py-0.5 rounded-full bg-white border border-sky-300 text-sky-700 font-medium hover:bg-sky-100 disabled:opacity-60">
            {busy === "email" ? <Loader2 size={10} className="animate-spin inline" /> : "Get email"}
          </button>
        )
      )}
      <button onClick={draft} disabled={!!busy} title={`Draft a mail personalized to ${contact.name || "this person"}`}
        className={`px-2 py-0.5 rounded-full bg-white border font-medium disabled:opacity-60 ${contact.email ? "border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "border-purple-300 text-purple-700 hover:bg-purple-100"}`}>
        {busy === "draft" ? <Loader2 size={10} className="animate-spin inline" /> : <><Sparkles size={9} className="inline mr-0.5" />Draft</>}
      </button>
      {note && <span className="text-[10px] text-stone-500 max-w-[180px] truncate" title={note}>{note}</span>}
    </span>
  );
}

// Modal showing the agent-drafted outreach email with copy + mailto.
function OutreachModal({ lead, draft, onClose }) {
  const [copied, setCopied] = useState(null);
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  const copy = (what, text) => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(what); setTimeout(() => setCopied(null), 1500); });
  };
  const to = draft.to || (lead.hr_contacts || []).find((c) => c.email)?.email || "";
  const mailto = `mailto:${to}?subject=${encodeURIComponent(draft.subject)}&body=${encodeURIComponent(draft.body)}`;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] flex flex-col" data-testid="outreach-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div>
            <h2 className="font-display text-2xl">Outreach to {lead.company_name}</h2>
            <p className="text-sm text-stone-500">Drafted by your AI officer — review, copy, send from your own inbox.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto px-6 py-5">
          {to && <p className="text-xs text-stone-500 mb-2">To: <span className="font-mono text-stone-800">{to}</span></p>}
          <div className="rounded-2xl border border-stone-200 bg-white p-4">
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-semibold text-stone-900">{draft.subject}</p>
              <button onClick={() => copy("subject", draft.subject)} className="text-xs text-stone-400 hover:text-stone-900 shrink-0">
                {copied === "subject" ? "Copied ✓" : "Copy"}
              </button>
            </div>
            <div className="mt-3 pt-3 border-t border-stone-100 flex items-start justify-between gap-2">
              <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{draft.body}</p>
              <button onClick={() => copy("body", draft.body)} className="text-xs text-stone-400 hover:text-stone-900 shrink-0">
                {copied === "body" ? "Copied ✓" : "Copy"}
              </button>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <a href={mailto} className="btn-primary flex-1 justify-center text-sm"><Mail size={15} /> Open in email app</a>
            <button onClick={() => copy("all", `Subject: ${draft.subject}\n\n${draft.body}`)} className="btn-ghost flex-1 justify-center text-sm">
              <Copy size={15} /> {copied === "all" ? "Copied ✓" : "Copy everything"}
            </button>
          </div>
          <p className="mt-3 text-xs text-stone-400">Sent from your own email address — replies land with you, not Wishire.</p>
        </div>
      </div>
    </div>
  );
}

/* Type any company name → HR/CXO contacts with emails → drafted outreach.
   Creates a manual lead under the hood so the whole lead toolchain applies. */
function CompanyLookup({ onAdded }) {
  const [q, setQ] = useState("");
  const [stage, setStage] = useState(null); // null | "domain" | "emails" | "done"
  const [lead, setLead] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [note, setNote] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [busy, setBusy] = useState(null);

  const search = async (e) => {
    e?.preventDefault();
    const name = q.trim();
    if (!name || stage === "domain" || stage === "emails") return;
    setLead(null); setContacts([]); setNote(null);
    setStage("domain");
    try {
      const { data } = await lookupCompany(name);
      setLead(data.lead);
      if (data.lead.hr_contacts?.length) {
        setContacts(data.lead.hr_contacts); setStage("done");
        return;
      }
      setStage("emails");
      try {
        const { data: r } = await enrichLeadEmails(data.lead.lead_id);
        setContacts(r.hr_contacts || []);
      } catch (err) {
        // no emails at HR/CXO level — fall back to LinkedIn profiles
        setNote(err?.response?.data?.detail);
        try {
          const { data: h } = await findLeadHr(data.lead.lead_id);
          setContacts(h.hr_contacts || []);
        } catch (_) {}
      }
      setStage("done");
    } catch (err) {
      setNote(err?.response?.data?.detail || "Lookup failed — try again.");
      setStage("done");
    }
  };

  const draftMail = async () => {
    setBusy("draft");
    try {
      const { data: r } = await draftLeadOutreach(lead.lead_id);
      setOutreach({ lead: { ...lead, hr_contacts: contacts }, draft: r });
    } catch (err) { setNote(err?.response?.data?.detail || "Could not draft."); }
    finally { setBusy(null); }
  };

  const addToCrm = async () => {
    setBusy("add");
    try {
      await addLeadToCrm(lead.lead_id);
      setNote(`${lead.company_name} added to your recruiter list.`);
      onAdded?.();
    } catch (err) { setNote(err?.response?.data?.detail || "Could not add."); }
    finally { setBusy(null); }
  };

  const searching = stage === "domain" || stage === "emails";

  return (
    <div className="mb-6 rounded-3xl bg-stone-900 text-stone-50 p-6 relative overflow-hidden wsh-grain" data-testid="cm-lookup">
      {outreach && <OutreachModal lead={outreach.lead} draft={outreach.draft} onClose={() => setOutreach(null)} />}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(420px 240px at 92% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
      <div className="relative">
        <h2 className="font-display text-2xl flex items-center gap-2"><Search size={19} className="text-amber-300" /> Reach any company</h2>
        <p className="text-sm text-stone-400 mt-1">Type a company — we find their HR &amp; CXO contacts with emails and draft your outreach.</p>
        <form onSubmit={search} className="mt-4 flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="cm-lookup-input"
            placeholder="e.g. Infosys, Zoho, Bosch India…"
            className="flex-1 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-stone-50 placeholder:text-stone-500 focus:outline-none focus:border-amber-300 text-sm" />
          <button type="submit" disabled={searching || !q.trim()} data-testid="cm-lookup-btn"
            className="px-5 py-2.5 rounded-full bg-amber-300 text-stone-900 text-sm font-medium hover:bg-amber-200 disabled:opacity-50">
            {searching ? <Loader2 size={15} className="animate-spin" /> : "Find contacts"}
          </button>
        </form>

        {searching && (
          <p className="mt-4 text-sm text-stone-400 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" />
            {stage === "domain" ? "Finding the company's website…" : `Looking up HR & CXO contacts${lead?.company_domain ? ` at ${lead.company_domain}` : ""}…`}
          </p>
        )}
        {note && stage === "done" && <p className="mt-4 text-sm rounded-xl bg-white/10 border border-white/10 px-4 py-2.5 text-stone-300" data-testid="cm-lookup-note">{note}</p>}

        {stage === "done" && lead && (
          <div className="mt-4 rounded-2xl bg-white text-stone-900 p-4" data-testid="cm-lookup-result">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-semibold">{lead.company_name}
                  {lead.company_domain && <span className="ml-2 text-xs text-stone-400 font-mono">{lead.company_domain}</span>}
                </p>
                <p className="text-xs text-stone-500">{contacts.length ? `${contacts.length} contact${contacts.length !== 1 ? "s" : ""} found` : "No contacts surfaced yet"}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={draftMail} disabled={busy === "draft"} data-testid="cm-lookup-draft"
                  className="text-xs font-medium px-3 py-1.5 rounded-full border border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-60">
                  {busy === "draft" ? <Loader2 size={12} className="animate-spin inline" /> : <><Sparkles size={11} className="inline mr-1" />Draft outreach</>}
                </button>
                <button onClick={addToCrm} disabled={busy === "add"} data-testid="cm-lookup-add"
                  className="text-xs font-medium px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60">
                  {busy === "add" ? <Loader2 size={12} className="animate-spin inline" /> : "+ Add to CRM"}
                </button>
              </div>
            </div>
            {contacts.length > 0 && (
              <div className="mt-3 pt-3 border-t border-stone-100 flex flex-wrap gap-2">
                {contacts.map((c, i) => (
                  <ContactChip key={c.linkedin_url || c.email || i} lead={lead} contact={c}
                    onUpdated={(nc) => setContacts((arr) => arr.map((cc) => (cc.linkedin_url === nc.linkedin_url ? nc : cc)))}
                    onDraft={(draft) => setOutreach({ lead: { ...lead, hr_contacts: contacts }, draft })}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LeadsSection({ onAdded }) {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState(null); // `${lead_id}:${action}` in flight
  const [hrOpen, setHrOpen] = useState({}); // lead_id -> contacts
  const [msg, setMsg] = useState(null);
  const [srcFilter, setSrcFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");
  const [depts, setDepts] = useState([]); // the college's own departments
  const [shown, setShown] = useState(20);
  const [outreach, setOutreach] = useState(null); // {lead, draft}
  const load = () => listLeads().then(({ data }) => setData(data)).catch(() => setData({ leads: [] }));
  useEffect(() => {
    load();
    listDepartments().then(({ data }) => setDepts((data.departments || []).map((d) => d.name))).catch(() => {});
  }, []);

  const refresh = async () => {
    setRefreshing(true); setMsg(null);
    try {
      const { data: r } = await refreshLeads();
      if (r.refreshed) {
        const bits = [`${r.added} new`];
        if (r.updated) bits.push(`${r.updated} updated`);
        if (r.purged) bits.push(`${r.purged} closed listing${r.purged === 1 ? "" : "s"} removed`);
        setMsg(`Sweep complete — ${bits.join(", ")}.`);
      } else {
        const mins = r.next_in_minutes || 0;
        const d = Math.floor(mins / 1440), h = Math.floor((mins % 1440) / 60);
        const when = d ? `${d}d ${h}h` : h ? `${h}h ${mins % 60}m` : `${mins}m`;
        setMsg(`Company data refreshes weekly to keep costs down — next full sweep in ${when}. Need a specific company now? Use "Reach any company" above (1 credit).`);
      }
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not refresh leads."); }
    finally { setRefreshing(false); }
  };

  const act = async (l, action, fn) => {
    setBusy(`${l.lead_id}:${action}`); setMsg(null);
    try { await fn(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Action failed — try again."); }
    finally { setBusy(null); }
  };

  const findHr = (l) => act(l, "hr", async () => {
    const { data: r } = await findLeadHr(l.lead_id);
    setHrOpen((x) => ({ ...x, [l.lead_id]: r.hr_contacts }));
  });

  const getEmails = (l) => act(l, "email", async () => {
    const { data: r } = await enrichLeadEmails(l.lead_id);
    setHrOpen((x) => ({ ...x, [l.lead_id]: r.hr_contacts }));
    load(); // lead now carries emails + domain
  });

  const draftMail = (l) => act(l, "draft", async () => {
    const { data: r } = await draftLeadOutreach(l.lead_id);
    setOutreach({ lead: { ...l, hr_contacts: hrOpen[l.lead_id] || l.hr_contacts || [] }, draft: r });
  });

  const addToCrm = (l) => act(l, "add", async () => {
    await addLeadToCrm(l.lead_id);
    setMsg(`${l.company_name} added to your recruiter list as a prospect${(hrOpen[l.lead_id] || l.hr_contacts || []).some((c) => c.email) ? " — with their HR email" : ""}.`);
    load(); onAdded?.();
  });

  const all = data?.leads || [];
  const sources = ["all", ...Array.from(new Set(all.map((l) => l.source)))];
  // Department fit: a lead matches when its role's buckets intersect the
  // selected department's buckets (e.g. CSE ∩ "Fresher SDE" → software).
  const deptBuckets = deptFilter === "all" ? null : textBuckets(deptFilter);
  const leads = all.filter((l) =>
    (srcFilter === "all" || l.source === srcFilter) &&
    (!deptBuckets || textBuckets(`${l.role || ""} ${l.evidence_title || ""}`).some((b) => deptBuckets.includes(b)))
  );

  return (
    <div className="mb-10 rounded-3xl border border-amber-200 bg-amber-50/50 p-6" data-testid="cm-leads">
      {outreach && <OutreachModal lead={outreach.lead} draft={outreach.draft} onClose={() => setOutreach(null)} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl flex items-center gap-2"><Sparkles size={20} className="text-[#C2410C]" /> Companies hiring freshers right now</h2>
          <p className="text-sm text-stone-600 mt-1">
            Swept weekly from LinkedIn, Naukri, Indeed & more — get the HR contact, let the agent draft your outreach, add to CRM. Need a company that isn't listed? Search it above (credits apply).
            {data?.last_run && <span className="text-stone-400"> · Last sweep {new Date(data.last_run).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.credits && (
            <span data-testid="cm-credits"
              title={`Credits pay for lookups: company lookup ${data.credits.costs.company_lookup} · Find HR ${data.credits.costs.find_hr} · verified emails ${data.credits.costs.enrich_emails} · one contact's email ${data.credits.costs.contact_email} · AI draft ${data.credits.costs.draft_outreach}. Cached results are free; failed lookups are refunded.`}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${data.credits.balance > 0 ? "border-amber-300 bg-amber-100 text-amber-900" : "border-red-300 bg-red-50 text-red-700"}`}>
              <Sparkles size={12} /> {data.credits.balance.toLocaleString("en-IN")} credits
            </span>
          )}
          <button onClick={refresh} disabled={refreshing} data-testid="cm-leads-refresh" className="btn-ghost text-sm disabled:opacity-60">
            {refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} {refreshing ? "Sweeping job boards…" : "Refresh leads"}
          </button>
        </div>
      </div>
      {msg && <p className="mt-3 text-sm rounded-xl bg-white border border-amber-200 text-stone-700 px-4 py-2.5" data-testid="cm-leads-msg">{msg}</p>}

      {/* Source filter */}
      {all.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-1.5" data-testid="cm-leads-filters">
          {sources.map((s) => (
            <button key={s} onClick={() => { setSrcFilter(s); setShown(20); }}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${srcFilter === s ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:bg-white"}`}>
              {s === "all" ? `All (${all.length})` : `${s} (${all.filter((l) => l.source === s).length})`}
            </button>
          ))}
        </div>
      )}
      {/* Department-fit filter — the college's own departments */}
      {all.length > 0 && depts.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="cm-leads-dept-filters">
          <span className="text-[10px] font-mono uppercase tracking-widest text-stone-400 mr-1">Best fit for</span>
          {["all", ...depts].map((d) => {
            const bs = d === "all" ? null : textBuckets(d);
            const n = d === "all" ? all.length : all.filter((l) =>
              textBuckets(`${l.role || ""} ${l.evidence_title || ""}`).some((b) => bs.includes(b))).length;
            return (
              <button key={d} onClick={() => { setDeptFilter(d); setShown(20); }}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${deptFilter === d ? "bg-[#C2410C] text-white border-[#C2410C]" : "border-stone-300 text-stone-600 hover:bg-white"}`}>
                {d === "all" ? "All departments" : `${d} (${n})`}
              </button>
            );
          })}
        </div>
      )}

      {data === null ? (
        <p className="mt-4 text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading leads…</p>
      ) : leads.length === 0 ? (
        <p className="mt-4 text-sm text-stone-500">No new leads yet — hit "Refresh leads" to run a discovery sweep.</p>
      ) : (
        <div className="mt-4 space-y-2">
          {leads.slice(0, shown).map((l) => {
            const contacts = hrOpen[l.lead_id] || l.hr_contacts || [];
            const hasEmail = contacts.some((c) => c.email);
            const b = (a) => busy === `${l.lead_id}:${a}`;
            return (
              <div key={l.lead_id} className="rounded-2xl border border-stone-200 bg-white px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-stone-900">
                      {l.company_name || <span className="text-stone-400 font-normal">Company on listing</span>}
                      <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${SOURCE_STYLE[l.source] || SOURCE_STYLE.Web}`}>{l.source}</span>
                      {l.company_domain && <span className="ml-1.5 text-[10px] text-stone-400 font-mono">{l.company_domain}</span>}
                    </p>
                    <p className="text-xs text-stone-500 truncate max-w-[420px]">{l.role}{l.location ? ` · ${l.location}` : ""}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 max-w-full">
                    <a href={l.evidence_url} target="_blank" rel="noreferrer" className="text-xs text-stone-400 hover:text-stone-900 inline-flex items-center gap-1">Listing <ExternalLink size={11} /></a>
                    {l.company_name && (
                      <>
                        {contacts.length === 0 && (
                          <button onClick={() => findHr(l)} disabled={b("hr")} data-testid={`lead-hr-${l.lead_id}`}
                            className="text-xs font-medium px-3 py-1.5 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 disabled:opacity-60">
                            {b("hr") ? <Loader2 size={12} className="animate-spin inline" /> : <>Find HR{data?.credits ? ` · ${data.credits.costs.find_hr} cr` : ""}</>}
                          </button>
                        )}
                        {!hasEmail && (
                          <button onClick={() => getEmails(l)} disabled={b("email")} data-testid={`lead-email-${l.lead_id}`}
                            title="Find verified HR email addresses (Apollo)"
                            className="text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60">
                            {b("email") ? <Loader2 size={12} className="animate-spin inline" /> : <><Mail size={11} className="inline mr-1" />Get emails{data?.credits ? ` · ${data.credits.costs.enrich_emails} cr` : ""}</>}
                          </button>
                        )}
                        <button onClick={() => draftMail(l)} disabled={b("draft")} data-testid={`lead-draft-${l.lead_id}`}
                          title="Your AI officer drafts the outreach email with your real batch stats"
                          className="text-xs font-medium px-3 py-1.5 rounded-full border border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-60">
                          {b("draft") ? <Loader2 size={12} className="animate-spin inline" /> : <><Sparkles size={11} className="inline mr-1" />Draft outreach</>}
                        </button>
                        <button onClick={() => addToCrm(l)} disabled={b("add")} data-testid={`lead-add-${l.lead_id}`}
                          className="text-xs font-medium px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60">
                          {b("add") ? <Loader2 size={12} className="animate-spin inline" /> : "+ Add to CRM"}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {contacts.length > 0 && (
                  <div className="mt-2.5 pt-2.5 border-t border-stone-100 flex flex-wrap gap-2" data-testid={`lead-contacts-${l.lead_id}`}>
                    {contacts.map((c, i) => (
                      <ContactChip key={c.linkedin_url || c.email || i} lead={l} contact={c}
                        onUpdated={(nc) => setHrOpen((x) => ({
                          ...x,
                          [l.lead_id]: contacts.map((cc) => (cc.linkedin_url === nc.linkedin_url ? nc : cc)),
                        }))}
                        onDraft={(draft) => setOutreach({ lead: { ...l, hr_contacts: contacts }, draft })}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {leads.length > shown && (
            <button onClick={() => setShown((n) => n + 20)} data-testid="cm-leads-more"
              className="w-full text-center text-sm text-stone-500 hover:text-stone-900 py-2.5 rounded-2xl border border-dashed border-stone-300 hover:border-stone-500">
              Show {Math.min(20, leads.length - shown)} more of {leads.length - shown}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function CampusRecruiters() {
  const [rows, setRows] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => listRecruiters().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const setStatus = async (r, status) => {
    setRows((arr) => arr.map((x) => (x.recruiter_id === r.recruiter_id ? { ...x, status } : x)));
    try { await updateRecruiter(r.recruiter_id, { status }); } catch (_) { load(); }
  };
  const remove = async (r) => {
    if (!(await confirm({ title: "Please confirm", description: `Remove ${r.company_name} from your recruiter list?`, danger: true }))) return;
    try { await deleteRecruiter(r.recruiter_id); load(); } catch (_) {}
  };
  const [viewing, setViewing] = useState(null);
  const [pipe, setPipe] = useState(null);
  const loadPipe = () => recruiterPipeline().then(({ data }) => setPipe(data)).catch(() => {});
  useEffect(() => { loadPipe(); }, []);

  return (
    <div className="container-x py-10 md:py-14">
      {viewing && <RecruiterDrawer recruiter={viewing} onClose={() => setViewing(null)} onEdit={() => { setEditing(viewing); setViewing(null); }} onChanged={() => { load(); loadPipe(); }} />}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline">Recruiters</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Your company relationships.</h1>
          <p className="mt-2 text-stone-600 max-w-xl">Every company that runs a drive shows up here automatically. Track contacts, who owns the relationship, and where each company stands this season.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="cm-rec-add" className="btn-primary shrink-0"><Plus size={16} /> Add company</button>
      </div>

      {showAdd && <RecruiterModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      {editing && <RecruiterModal recruiter={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {pipe && <PipelineStrip pipe={pipe} rows={rows || []} onOpen={(r) => setViewing(r)} onChanged={loadPipe} />}
      <CompanyLookup onAdded={load} />
      <LeadsSection onAdded={load} />

      {rows === null ? <p className="text-stone-500">Loading…</p> : rows.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-12 text-center text-stone-500">
          No recruiters yet. Post a drive and the company appears here automatically — or add one you're courting.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          <table className="w-full text-sm" data-testid="cm-rec-table">
            <thead><tr className="border-b border-stone-100 text-xs font-mono uppercase tracking-widest text-stone-400">
              {["Company", "Contact", "Owner", "Drives", "Offers", "Best pkg", "Last drive", "Status", ""].map((h, i) => <th key={i} className="text-left px-4 py-3 font-normal">{h}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.recruiter_id} className="border-b border-stone-50 hover:bg-stone-50">
                  <td className="px-4 py-3">
                    <button onClick={() => setViewing(r)} data-testid={`cm-rec-open-${r.recruiter_id}`} className="font-medium text-stone-900 hover:underline text-left">{r.company_name}</button>
                    {pipe?.health?.[r.recruiter_id] && pipe.health[r.recruiter_id] !== "prospect" && (
                      <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full align-middle ${pipe.health[r.recruiter_id] === "at_risk" ? "bg-red-100 text-red-700" : pipe.health[r.recruiter_id] === "lapsed" ? "bg-stone-200 text-stone-600" : "bg-emerald-100 text-emerald-800"}`}>{pipe.health[r.recruiter_id].replace("_", " ")}</span>
                    )}
                    {r.notes && <p className="text-xs text-stone-400 truncate max-w-[220px]">{r.notes}</p>}
                  </td>
                  <td className="px-4 py-3 text-stone-600">
                    {r.contact_name || "—"}
                    {r.contact_email && <p className="text-xs text-stone-400">{r.contact_email}</p>}
                  </td>
                  <td className="px-4 py-3 text-stone-600">{r.owner || "—"}</td>
                  <td className="px-4 py-3 font-mono">{r.drives_count}</td>
                  <td className="px-4 py-3 font-mono text-emerald-700">{r.offers_made}</td>
                  <td className="px-4 py-3 font-mono">{r.best_lpa != null ? `₹${r.best_lpa}L` : "—"}</td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{r.last_drive_at ? new Date(r.last_drive_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td>
                  <td className="px-4 py-3">
                    <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`cm-rec-status-${r.recruiter_id}`}
                      className={`text-xs px-2 py-1 rounded-full border-0 cursor-pointer font-semibold ${REC_STATUS[r.status]?.cls || REC_STATUS.prospect.cls}`}>
                      {Object.entries(REC_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(r)} className="p-1.5 rounded-lg text-stone-300 hover:text-red-600 hover:bg-red-50" title="Remove"><Trash2 size={15} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* Season pipeline: who is back, who is slipping away, who to court, and what is due today. */
function PipelineStrip({ pipe, rows, onOpen, onChanged }) {
  const byId = Object.fromEntries(rows.map((r) => [r.recruiter_id, r]));
  const c = pipe.counts;
  const tile = (label, n, cls, key) => (
    <div key={label} className={`rounded-2xl border px-4 py-3 ${cls}`}>
      <p className="font-display text-3xl leading-none">{n}</p>
      <p className="text-[11px] mt-1 opacity-80">{label}</p>
      {key && n > 0 && <p className="mt-1.5 text-xs truncate">{pipe.buckets[key].slice(0, 3).map((r) => r.company_name).join(", ")}{n > 3 ? "…" : ""}</p>}
    </div>
  );
  const finish = async (f) => { try { await doneFollowup(f.followup_id); onChanged(); } catch (_) {} };
  return (
    <div className="mb-8" data-testid="rec-pipeline">
      <div className="flex items-center justify-between mb-3"><span className="overline">Season {pipe.season}</span><span className="text-xs text-stone-500">Click a company for its history, timeline, alumni and a drafted invite.</span></div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {tile("Returning", c.returning, "border-emerald-200 bg-emerald-50 text-emerald-900", "returning")}
        {tile("Active (first season)", c.active, "border-emerald-200 bg-white text-stone-900", "active")}
        {tile("At risk: no drive yet", c.at_risk, "border-red-200 bg-red-50 text-red-900", "at_risk")}
        {tile("Lapsed", c.lapsed, "border-stone-200 bg-stone-50 text-stone-700", "lapsed")}
        {tile("Prospects", c.prospect, "border-amber-200 bg-amber-50 text-amber-900", "prospect")}
      </div>
      {(pipe.followups_due.length > 0 || pipe.followups_upcoming.length > 0) && (
        <div className="mt-3 rounded-2xl border border-stone-200 bg-white p-4">
          <span className="overline">Follow-ups</span>
          <ul className="mt-2 space-y-1.5 text-sm">
            {pipe.followups_due.map((f) => (
              <li key={f.followup_id} className="flex items-center justify-between gap-3">
                <span className="text-red-700"><b>Due</b> · <button onClick={() => byId[f.recruiter_id] && onOpen(byId[f.recruiter_id])} className="underline">{f.company_name}</button>: {f.note}</span>
                <button onClick={() => finish(f)} className="text-xs px-2.5 py-1 rounded-full border border-stone-300 hover:border-emerald-600 hover:text-emerald-700">Done</button>
              </li>
            ))}
            {pipe.followups_upcoming.slice(0, 3).map((f) => (
              <li key={f.followup_id} className="text-stone-600">{new Date(f.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })} · <button onClick={() => byId[f.recruiter_id] && onOpen(byId[f.recruiter_id])} className="underline">{f.company_name}</button>: {f.note}</li>
            ))}
          </ul>
        </div>
      )}
      {c.at_risk > 0 && pipe.followups_due.length === 0 && (
        <p className="mt-3 text-sm text-red-800 bg-red-50 border border-red-200 rounded-2xl px-4 py-2.5">{c.at_risk} compan{c.at_risk === 1 ? "y" : "ies"} hired here before but ha{c.at_risk === 1 ? "s" : "ve"} no drive this season. Open each one and send the invite; the draft is written from your numbers.</p>
      )}
    </div>
  );
}

/* Forecast accuracy: predicted vs actual on closed drives, and the learned calibration. */
function ForecastAccuracyCard() {
  const [d, setD] = useState(null);
  useEffect(() => { forecastAccuracy().then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d || !d.drives.length) return null;
  const closed = d.drives.filter((x) => x.status === "closed" && x.actual != null);
  const hits = closed.filter((x) => x.actual >= x.low && x.actual <= x.high).length;
  return (
    <div className="mb-6 rounded-2xl border border-stone-200 bg-white px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="forecast-accuracy">
      <span className="overline">Forecasts</span>
      <span>{d.drives.length} drive{d.drives.length === 1 ? "" : "s"} forecast</span>
      {closed.length > 0 ? <span>{hits} of {closed.length} closed drives landed inside the forecast range</span> : <span className="text-stone-500">Close a drive to see predicted vs actual.</span>}
      <span className="text-stone-500">Calibration ×{d.calibration.factor} from {d.calibration.drives_learned_from} closed drive{d.calibration.drives_learned_from === 1 ? "" : "s"}{d.calibration.drives_learned_from < 2 ? " (learning)" : ""}</span>
    </div>
  );
}

function RecruiterModal({ recruiter, onClose, onSaved }) {
  const isEdit = !!recruiter;
  const [f, setF] = useState({
    company_name: recruiter?.company_name || "",
    contact_name: recruiter?.contact_name || "",
    contact_email: recruiter?.contact_email || "",
    contact_phone: recruiter?.contact_phone || "",
    website: recruiter?.website || "",
    owner: recruiter?.owner || "",
    notes: recruiter?.notes || "",
    status: recruiter?.status || "prospect",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.company_name.trim()) { setErr("Company name is required."); return; }
    setBusy(true); setErr("");
    const payload = {
      contact_name: f.contact_name.trim() || undefined,
      contact_email: f.contact_email.trim() || undefined,
      contact_phone: f.contact_phone.trim() || undefined,
      website: f.website.trim() || undefined,
      owner: f.owner.trim() || undefined,
      notes: f.notes.trim() || undefined,
      status: f.status,
    };
    try {
      if (isEdit) await updateRecruiter(recruiter.recruiter_id, payload);
      else await addRecruiter({ company_name: f.company_name.trim(), ...payload });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Could not save."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200 sticky top-0 bg-[#FDFBF7]">
          <h2 className="font-display text-2xl">{isEdit ? recruiter.company_name : "Add company"}</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          {!isEdit && <Field label="Company name *"><input value={f.company_name} onChange={set("company_name")} data-testid="cm-rec-name" placeholder="Infosys" className="cm-inp" /></Field>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Contact person"><input value={f.contact_name} onChange={set("contact_name")} data-testid="cm-rec-contact" placeholder="Priya Sharma" className="cm-inp" /></Field>
            <Field label="Contact email"><input value={f.contact_email} onChange={set("contact_email")} placeholder="priya@company.com" className="cm-inp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><input value={f.contact_phone} onChange={set("contact_phone")} placeholder="+91…" className="cm-inp" /></Field>
            <Field label="Website"><input value={f.website} onChange={set("website")} placeholder="company.com" className="cm-inp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Relationship owner"><input value={f.owner} onChange={set("owner")} placeholder="Who on your team owns this?" className="cm-inp" /></Field>
            <Field label="Status">
              <select value={f.status} onChange={set("status")} className="cm-inp">
                {Object.entries(REC_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Notes"><textarea value={f.notes} onChange={set("notes")} rows={3} placeholder="Met at HR conclave. Wants CS batch in October…" className="cm-inp" /></Field>
          {err && <p className="text-sm text-red-700">{err}</p>}
          <button type="submit" disabled={busy} data-testid="cm-rec-submit" className="btn-primary w-full justify-center disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <>{isEdit ? "Save changes" : "Add company"} <Building2 size={15} /></>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─────────── Student 360° drawer ─────────── */
function ScoreBar({ label, value, weight }) {
  const c = value >= 70 ? "#15803D" : value >= 40 ? "#D97706" : value > 0 ? "#EA580C" : "#D6D3D1";
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-stone-600">{label} {weight && <span className="text-stone-400">· {weight}</span>}</span>
        <span className="font-mono font-semibold" style={{ color: value ? c : "#A8A29E" }}>{value}</span>
      </div>
      <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, background: c }} />
      </div>
    </div>
  );
}

function ActivityStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-3.5">
      <div className="flex items-center gap-2 text-stone-400"><Icon size={14} /><span className="text-[10px] font-mono uppercase tracking-widest">{label}</span></div>
      <p className="font-display text-2xl mt-1">{value}</p>
      {sub && <p className="text-[11px] text-stone-400 mt-0.5">{sub}</p>}
    </div>
  );
}

const COURSE_STATUS_STYLE = {
  completed: "bg-emerald-100 text-emerald-700",
  started: "bg-blue-100 text-blue-700",
  not_started: "bg-stone-100 text-stone-500",
};

function StudentDrawer({ studentId, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [rep, setRep] = useState(false);
  useEffect(() => {
    studentProfile(studentId).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load student."));
  }, [studentId]);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-2xl bg-[#FDFBF7] h-full overflow-y-auto shadow-2xl" data-testid="student-drawer">
        {err ? (
          <div className="p-8"><p className="text-red-700">{err}</p><button onClick={onClose} className="btn-ghost mt-4">Close</button></div>
        ) : !d ? (
          <div className="p-8 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading student…</div>
        ) : (
          <>
            {/* Header */}
            <div className="sticky top-0 bg-[#FDFBF7] border-b border-stone-200 px-7 py-5 flex items-start justify-between z-10">
              <div>
                <h2 className="font-display text-3xl">{d.student.name}</h2>
                <p className="text-sm text-stone-500 mt-0.5">
                  {d.student.email} · {d.student.branch || "no dept"}{d.student.section ? `-${d.student.section}` : ""} · CGPA {d.student.cgpa ?? "—"}
                  {d.student.cgpa_verified && <ShieldCheck size={13} className="inline ml-1 text-emerald-600" />}
                  {d.student.grad_year ? ` · ’${String(d.student.grad_year).slice(2)}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${(TIER_STYLE[d.wishire.tier] || TIER_STYLE.developing).cls}`}>{(TIER_STYLE[d.wishire.tier] || TIER_STYLE.developing).label}</span>
                <button onClick={async () => { setRep(true); try { const { data } = await studentReport(studentId); if (!openCandidateReports(data, `${data.student.name} · candidate report`)) toast.error("Allow pop-ups to open the report."); } catch (_) { toast.error("Could not build the report."); } finally { setRep(false); } }}
                  data-testid="student-report" title="Recruiter-ready report (print or save as PDF)" className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-stone-300 hover:border-stone-900">
                  {rep ? <Loader2 size={13} className="animate-spin" /> : <FileText size={13} />} Recruiter report
                </button>
                <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
              </div>
            </div>

            <div className="px-7 py-6 space-y-8">
              {/* Wishire score breakdown */}
              <section>
                <div className="flex items-end justify-between mb-4">
                  <span className="overline">Wishire score
                    {d.wishire.confidence && (
                      <span className={`ml-2 normal-case tracking-normal font-sans text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                        d.wishire.confidence === "high" ? "bg-emerald-100 text-emerald-700"
                        : d.wishire.confidence === "medium" ? "bg-amber-100 text-amber-700"
                        : "bg-stone-200 text-stone-500"}`} data-testid="score-confidence"
                        title="How much evidence backs this score — attempts, freshness, clean proctoring">
                        {d.wishire.confidence} confidence
                      </span>
                    )}
                    {d.wishire.prep_bonus > 0 && (
                      <span className="ml-1.5 normal-case tracking-normal font-sans text-[10px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700" title="Consistent prep practice bonus">
                        +{d.wishire.prep_bonus} prep
                      </span>
                    )}
                  </span>
                  <p className="font-display text-5xl leading-none">{d.wishire.score}<span className="text-lg text-stone-400">/100</span></p>
                </div>
                <div className="space-y-3">
                  <ScoreBar label="Mock interviews" value={d.wishire.interview_score} weight={d.wishire.has_mock_test ? "35%" : "45%"} />
                  <ScoreBar label="Résumé" value={d.wishire.resume_score} weight={d.wishire.has_mock_test ? "25%" : "30%"} />
                  {d.wishire.has_mock_test && <ScoreBar label="Proctored test" value={d.wishire.mock_test} weight="20%" />}
                  <ScoreBar label="Skills match" value={d.wishire.skills} weight={d.wishire.has_mock_test ? "10%" : "15%"} />
                  <ScoreBar label="Communication" value={d.wishire.communication} weight="10%" />
                </div>
              </section>

              {/* Platform activity */}
              <section>
                <span className="overline">Platform activity</span>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <ActivityStat icon={Mic} label="Mock interviews" value={d.activity.interviews.count}
                    sub={d.activity.interviews.count ? `Last: ${d.activity.interviews.last_score}/100 · ${fmtDate(d.activity.interviews.last_at)}` : "Never taken"} />
                  <ActivityStat icon={FileText} label="Résumés scored" value={d.activity.resumes.count}
                    sub={d.activity.resumes.count ? `Last: ${d.activity.resumes.last_score}/100 · ${fmtDate(d.activity.resumes.last_at)}` : "None uploaded"} />
                  <ActivityStat icon={MessageSquare} label="Coach chats" value={d.activity.coach_messages}
                    sub={d.activity.coach_last_at ? `Last ${fmtDate(d.activity.coach_last_at)}` : "Never used"} />
                  <ActivityStat icon={Sparkles} label="Prep solved" value={d.activity.prep.solved ?? 0}
                    sub={`Streak ${d.activity.prep.streak ?? 0} · ${d.activity.prep.points ?? 0} pts`} />
                  <ActivityStat icon={Linkedin} label="LinkedIn" value={d.activity.linkedin_analyzed ? "✓" : "—"}
                    sub={d.activity.linkedin_analyzed ? "Analyzed" : "Not analyzed"} />
                  <ActivityStat icon={Bell} label="Nudges received" value={d.activity.nudges_received} sub="from your cell" />
                </div>
                {d.activity.coaching_plan && (
                  <p className="mt-3 text-sm rounded-xl bg-purple-50 border border-purple-200 text-purple-800 px-4 py-2.5">
                    <Sparkles size={13} className="inline mr-1.5" />AI coaching plan active — {d.activity.coaching_plan.headline || `targeting ${d.activity.coaching_plan.target_role}`}
                  </p>
                )}
              </section>

              {/* Suggested next steps */}
              {d.suggestions.length > 0 && (
                <section>
                  <span className="overline">Suggested next steps</span>
                  <ul className="mt-3 space-y-2" data-testid="student-suggestions">
                    {d.suggestions.map((s, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
                        <ArrowRight size={14} className="mt-0.5 shrink-0 text-amber-600" />{s}
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* College courses */}
              <section>
                <span className="overline">College courses</span>
                {d.courses.length === 0 ? (
                  <p className="mt-2 text-sm text-stone-500">No courses curated yet — add them from Students → Courses.</p>
                ) : (
                  <div className="mt-3 space-y-2" data-testid="student-courses">
                    {d.courses.map((c) => (
                      <div key={c.course_id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-stone-900 truncate">{c.title}</p>
                          {c.provider && <p className="text-xs text-stone-400">{c.provider}</p>}
                        </div>
                        <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold ${COURSE_STATUS_STYLE[c.status] || COURSE_STATUS_STYLE.not_started}`}>
                          {c.status === "not_started" ? "Not started" : c.status === "started" ? "In progress" : "Completed"}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Drive applications */}
              <section>
                <span className="overline">Drive applications</span>
                {d.applications.length === 0 ? (
                  <p className="mt-2 text-sm text-stone-500">Hasn't applied to any drives yet.</p>
                ) : (
                  <div className="mt-3 rounded-2xl border border-stone-200 bg-white overflow-hidden">
                    {d.applications.map((a, i) => (
                      <div key={i} className={`flex items-center justify-between px-4 py-3 ${i > 0 ? "border-t border-stone-100" : ""}`}>
                        <div><p className="text-sm font-medium">{a.drive_title}</p><p className="text-xs text-stone-500">{a.company_name} · {fmtDate(a.applied_at)}</p></div>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STAGE_STYLE[a.status] || STAGE_STYLE.applied}`}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─────────── College courses modal ─────────── */
function CoursesModal({ onClose }) {
  const [tab, setTab] = useState("courses");
  const [rows, setRows] = useState(null);
  const [sales, setSales] = useState(null);
  const [depts, setDepts] = useState([]);
  const [f, setF] = useState({ title: "", provider: "", url: "", price_inr: "", skills: "", required: false });
  const [selDepts, setSelDepts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [progressFor, setProgressFor] = useState(null);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const load = () => listCourses().then(({ data }) => setRows(data)).catch(() => setRows([]));
  const loadSales = () => courseSales().then(({ data }) => setSales(data)).catch(() => setSales(null));
  useEffect(() => { load(); loadSales(); listDepartments().then(({ data }) => setDepts((data.departments || []).map((x) => x.name))).catch(() => {}); }, []);
  const toggleDept = (d) => setSelDepts((s) => s.includes(d) ? s.filter((x) => x !== d) : [...s, d]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) { setErr("Course title is required."); return; }
    setBusy(true); setErr("");
    try {
      await addCourse({
        title: f.title.trim(), provider: f.provider.trim() || undefined, url: f.url.trim() || undefined,
        price_inr: f.price_inr ? Math.max(0, parseInt(f.price_inr, 10)) : 0,
        skills: f.skills.trim() ? f.skills.split(",").map((x) => x.trim()).filter(Boolean) : undefined,
        branches: selDepts.length ? selDepts : undefined,
        required: !!f.required,
      });
      setF({ title: "", provider: "", url: "", price_inr: "", skills: "", required: false }); setSelDepts([]); load(); loadSales();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not add course."); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!(await confirm({ title: "Please confirm", description: `Remove "${c.title}"? Student progress on it is deleted too.`, danger: true }))) return;
    try { await deleteCourse(c.course_id); load(); loadSales(); } catch (_) {}
  };
  const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()}>
      {progressFor && <CourseProgressPanel fetcher={() => courseProgress(progressFor.course_id)} onClose={() => setProgressFor(null)} />}
      <div className="w-full max-w-2xl bg-[#FDFBF7] rounded-3xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
          <div><h2 className="font-display text-2xl">College courses</h2><p className="text-sm text-stone-500">Offer training to your students — free or paid. You earn on every sale.</p></div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-stone-400 hover:bg-stone-100"><X size={18} /></button>
        </div>
        <div className="px-6 pt-4">
          <div className="inline-flex rounded-full bg-stone-100 p-1 text-sm">
            <button onClick={() => setTab("courses")} className={`px-4 py-1.5 rounded-full font-medium transition-colors ${tab === "courses" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Courses</button>
            <button onClick={() => setTab("sales")} data-testid="cm-course-sales-tab" className={`px-4 py-1.5 rounded-full font-medium transition-colors ${tab === "sales" ? "bg-white shadow-sm text-stone-900" : "text-stone-500"}`}>Sales &amp; earnings</button>
          </div>
        </div>

        {tab === "courses" ? (
        <div className="overflow-y-auto px-6 py-5">
          <form onSubmit={submit} className="rounded-2xl border border-stone-200 bg-white p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Course title *"><input value={f.title} onChange={set("title")} data-testid="cm-course-title" placeholder="Full-Stack Bootcamp" className="cm-inp" /></Field>
              <Field label="Provider"><input value={f.provider} onChange={set("provider")} placeholder="NPTEL / in-house / Coursera" className="cm-inp" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Price (₹) — 0 = free"><input type="number" min="0" value={f.price_inr} onChange={set("price_inr")} data-testid="cm-course-price" placeholder="0" className="cm-inp" /></Field>
              <Field label="Skills taught (comma-sep)"><input value={f.skills} onChange={set("skills")} placeholder="react, node, sql" className="cm-inp" /></Field>
            </div>
            <Field label="Link (where students take it)"><input value={f.url} onChange={set("url")} placeholder="https://…" className="cm-inp" /></Field>
            <div>
              <label className="overline">For departments {selDepts.length > 0 && <span className="text-stone-400">· {selDepts.length}</span>}</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {depts.map((dep) => (
                  <button type="button" key={dep} onClick={() => toggleDept(dep)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${selDepts.includes(dep) ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-600 hover:border-stone-900"}`}>
                    {dep}
                  </button>
                ))}
                {depts.length === 0 && <span className="text-xs text-stone-400">No departments defined — course goes to everyone.</span>}
              </div>
              <p className="mt-1 text-xs text-stone-400">None selected = all students. Skills + department power the recommendations students see.</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-700"><input type="checkbox" checked={!!f.required} onChange={(e) => setF((x) => ({ ...x, required: e.target.checked }))} data-testid="cm-course-required" /> Recommend to every student until they finish it (shows on their Dashboard and plan)</label>
            {err && <p className="text-sm text-red-700">{err}</p>}
            <button type="submit" disabled={busy} data-testid="cm-course-add" className="btn-primary w-full justify-center disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <><Plus size={15} /> Add course</>}
            </button>
          </form>

          <div className="mt-5 space-y-2" data-testid="cm-course-list">
            {rows === null ? <p className="text-stone-500 text-sm">Loading…</p> : rows.length === 0 ? (
              <p className="text-sm text-stone-500 text-center py-6">No courses yet — add your first above.</p>
            ) : rows.map((c) => (
              <div key={c.course_id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-stone-900 flex items-center gap-1.5">
                    {c.title}
                    {c.url && <a href={c.url} target="_blank" rel="noreferrer" className="text-stone-400 hover:text-stone-900"><ExternalLink size={12} /></a>}
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${c.price_inr > 0 ? "bg-emerald-50 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>{c.price_inr > 0 ? money(c.price_inr) : "Free"}</span>
                  </p>
                  <p className="text-xs text-stone-400">
                    {c.provider || "—"} · {(c.branches || []).length ? c.branches.join(", ") : "All departments"}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setProgressFor(c)} data-testid={`cm-course-progress-${c.course_id}`} className="text-right group" title="Who has completed this course">
                    <span className="block text-xs font-mono text-stone-700"><b className="text-emerald-700">{c.completed}</b>/{c.eligible} done · {c.started} in progress</span>
                    <span className="block mt-1 h-1.5 w-28 ml-auto rounded-full bg-stone-100 overflow-hidden"><span className="block h-full bg-emerald-500" style={{ width: `${c.percent || 0}%` }} /></span>
                    <span className="block text-[10px] text-stone-400 group-hover:text-[#C2410C]">{c.price_inr > 0 ? <>{c.sold || 0} sold · {money(c.earnings_inr)} · </> : null}view progress →</span>
                  </button>
                  <button onClick={() => remove(c)} className="p-1.5 rounded-lg text-stone-300 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
        ) : (
        <div className="overflow-y-auto px-6 py-5" data-testid="cm-course-sales">
          {sales === null ? <p className="text-stone-500 text-sm">Loading…</p> : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-center"><p className="font-display text-2xl">{sales.totals.count}</p><p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Courses sold</p></div>
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center"><p className="font-display text-2xl text-emerald-700">{money(sales.totals.earnings_inr)}</p><p className="text-[10px] font-mono uppercase tracking-widest text-emerald-600">Your earnings</p></div>
              <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3 text-center"><p className="font-display text-2xl">{money(sales.totals.gross_inr)}</p><p className="text-[10px] font-mono uppercase tracking-widest text-stone-400">Gross · {sales.margin_pct}% fee</p></div>
            </div>
            <div className="mt-5 space-y-2">
              {sales.sales.length === 0 ? (
                <p className="text-sm text-stone-500 text-center py-6">No sales yet. Add a paid course and students can buy it from their campus page.</p>
              ) : sales.sales.map((r) => (
                <div key={r.sale_id} className="rounded-xl border border-stone-200 bg-white px-4 py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-stone-900 truncate">{r.student_name || "Student"} <span className="text-stone-400 font-normal">· {r.student_email}</span></p>
                      <p className="text-xs text-stone-500 truncate">{r.course_title}{r.student_branch ? ` · ${r.student_branch}` : ""} · {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-emerald-700">+{money(r.college_earning_inr)}</p>
                      <p className="text-[10px] text-stone-400">{money(r.price_inr)} − {money(r.wishire_cut_inr)} fee</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

function CopilotChat() {
  const [q, setQ] = useState("");
  const [msgs, setMsgs] = useState([]); // {role, text}
  const [busy, setBusy] = useState(false);

  const ask = async (e) => {
    e?.preventDefault();
    const question = q.trim();
    if (!question || busy) return;
    setMsgs((m) => [...m, { role: "user", text: question }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await copilotAsk(question);
      setMsgs((m) => [...m, { role: "assistant", text: data.answer }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "assistant", text: err?.response?.data?.detail || "Couldn't answer that — try again." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl bg-stone-900 text-stone-50 p-6 wsh-grain relative overflow-hidden" data-testid="copilot">
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ background: "radial-gradient(360px 240px at 90% 0%, rgba(217,119,6,0.4), transparent 60%)" }} />
      <div className="relative">
        <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>Copilot</span>
        <h2 className="font-display text-2xl mt-1">Ask about your placement season.</h2>
        <div className="mt-4 space-y-3 max-h-72 overflow-y-auto pr-1">
          {msgs.length === 0 && (
            <div className="flex flex-wrap gap-2">
              {["Who are my weakest students right now?", "Which drives close soon and who should apply?", "Summarize batch readiness for management"].map((s) => (
                <button key={s} onClick={() => { setQ(s); }} className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-stone-300 hover:bg-white/20">{s}</button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} className={`text-sm rounded-2xl px-4 py-2.5 whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-amber-300/90 text-stone-900 ml-8" : "bg-white/10 border border-white/10 mr-4"}`}>
              {m.text}
            </div>
          ))}
          {busy && <p className="text-sm text-stone-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Thinking over your batch…</p>}
        </div>
        <form onSubmit={ask} className="mt-4 flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="copilot-input"
            placeholder='e.g. "Which ECE students are job-ready?"'
            className="flex-1 px-4 py-2.5 rounded-full bg-white/10 border border-white/20 text-stone-50 placeholder:text-stone-500 focus:outline-none focus:border-amber-300 text-sm" />
          <button type="submit" disabled={busy || !q.trim()} data-testid="copilot-send" className="px-4 py-2.5 rounded-full bg-amber-300 text-stone-900 text-sm font-medium hover:bg-amber-200 disabled:opacity-50">
            Ask
          </button>
        </form>
      </div>
    </div>
  );
}

const AGENT_KIND_META = {
  start_journey: { label: "Not started", cls: "bg-stone-100 text-stone-600" },
  improve_resume: { label: "Résumé", cls: "bg-orange-100 text-orange-700" },
  practice_interview: { label: "Interview", cls: "bg-purple-100 text-purple-700" },
  start_course: { label: "Course", cls: "bg-blue-100 text-blue-700" },
  apply_drives: { label: "Apply now", cls: "bg-emerald-100 text-emerald-700" },
};

export function CampusAgent() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(null); // action_id
  const [expanded, setExpanded] = useState(null);
  const [msg, setMsg] = useState(null);
  const load = () => agentActions().then(({ data }) => setData(data)).catch(() => setData({ proposed: [], history: [] }));
  useEffect(() => { load(); }, []);

  const run = async () => {
    setRunning(true); setMsg(null);
    try {
      const { data: r } = await agentRun();
      setMsg(`Agent scanned ${r.students_scanned} students — ${r.proposed} new proposal${r.proposed === 1 ? "" : "s"}${r.sent ? `, ${r.sent} sent on autopilot` : ""}.`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not run the agent."); }
    finally { setRunning(false); }
  };

  const act = async (fn, id) => {
    setBusy(id ?? "all"); setMsg(null);
    try { await fn(); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Action failed."); }
    finally { setBusy(null); }
  };

  const toggleAutopilot = async () => {
    try { await agentAutopilot(!data.autopilot); load(); }
    catch (e) { setMsg(e?.response?.data?.detail || "Only the owner can change autopilot."); }
  };

  if (!data) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Waking your AI officer…</div>;

  const proposed = data.proposed || [];

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <div>
          <span className="overline">AI Placement Officer</span>
          <h1 className="font-display text-4xl md:text-5xl mt-2">It works while you sleep.</h1>
          <p className="mt-2 text-stone-600 max-w-xl">
            Every day the agent reviews each student's Wishire Score and activity, then drafts the right intervention.
            You approve — or flip on autopilot and let it send.
            {data.last_run && <span className="text-stone-400"> Last run {new Date(data.last_run).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}.</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer" title="When on, the agent sends its nudges automatically each day.">
            <button onClick={toggleAutopilot} data-testid="agent-autopilot" role="switch" aria-checked={data.autopilot}
              className={`w-11 h-6 rounded-full transition-colors relative ${data.autopilot ? "bg-emerald-500" : "bg-stone-300"}`}>
              <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${data.autopilot ? "left-[22px]" : "left-0.5"}`} />
            </button>
            Autopilot
          </label>
          <button onClick={run} disabled={running} data-testid="agent-run" className="btn-primary disabled:opacity-60">
            {running ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Run now
          </button>
        </div>
      </div>

      {msg && <p className="mb-6 text-sm rounded-xl bg-amber-50 border border-amber-200 text-stone-700 px-4 py-2.5" data-testid="agent-msg">{msg}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 items-start">
        <div>
          {/* Approval queue */}
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl">Waiting for your approval <span className="text-stone-400 text-lg">· {proposed.length}</span></h2>
            {proposed.length > 1 && (
              <button onClick={() => act(agentApproveAll)} disabled={busy === "all"} data-testid="agent-approve-all"
                className="text-sm font-medium text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5 disabled:opacity-60">
                {busy === "all" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approve & send all
              </button>
            )}
          </div>
          {proposed.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center text-stone-500">
              Queue is clear. The agent runs daily — or hit "Run now" to scan your batch immediately.
            </div>
          ) : (
            <div className="space-y-2" data-testid="agent-queue">
              {proposed.map((a) => {
                const k = AGENT_KIND_META[a.kind] || AGENT_KIND_META.start_journey;
                const open = expanded === a.action_id;
                return (
                  <div key={a.action_id} className="rounded-2xl border border-stone-200 bg-white px-5 py-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <button onClick={() => setExpanded(open ? null : a.action_id)} className="text-left min-w-0">
                        <p className="text-sm font-semibold text-stone-900">
                          {a.student_name}
                          <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${k.cls}`}>{k.label}</span>
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">{a.reason}</p>
                      </button>
                      <div className="flex flex-wrap items-center gap-2 max-w-full">
                        <button onClick={() => act(() => agentDismiss(a.action_id), a.action_id)} disabled={busy === a.action_id}
                          className="text-xs px-3 py-1.5 rounded-full border border-stone-300 text-stone-500 hover:border-stone-900 hover:text-stone-900 disabled:opacity-60">
                          Dismiss
                        </button>
                        <button onClick={() => act(() => agentApprove(a.action_id), a.action_id)} disabled={busy === a.action_id} data-testid={`agent-approve-${a.action_id}`}
                          className="text-xs font-medium px-3 py-1.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-60">
                          {busy === a.action_id ? <Loader2 size={12} className="animate-spin inline" /> : "Approve & send"}
                        </button>
                      </div>
                    </div>
                    {open && (
                      <div className="mt-3 pt-3 border-t border-stone-100 text-sm">
                        <p className="font-medium text-stone-800">✉ {a.subject}</p>
                        <p className="mt-1.5 text-stone-600 whitespace-pre-wrap leading-relaxed">{a.body}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Activity history */}
          {(data.history || []).length > 0 && (
            <>
              <h2 className="font-display text-2xl mt-10 mb-3">Recent activity</h2>
              <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
                {data.history.map((a, i) => (
                  <div key={a.action_id} className={`flex items-center justify-between px-5 py-3 ${i > 0 ? "border-t border-stone-100" : ""}`}>
                    <div className="min-w-0">
                      <p className="text-sm text-stone-800 truncate">{a.student_name} — {a.subject}</p>
                      <p className="text-xs text-stone-400">{a.decided_at ? new Date(a.decided_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</p>
                    </div>
                    <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-semibold ${a.status === "sent" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-500"}`}>
                      {a.status === "sent" ? "Sent" : "Dismissed"}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Right rail: copilot */}
        <CopilotChat />
      </div>
    </div>
  );
}

/* ─────────── Profile (college settings) ─────────── */
export function CampusProfile() {
  const [co, setCo] = useState(null);
  const [f, setF] = useState({ name: "", website: "", about: "" });
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [codeBusy, setCodeBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const [showDepts, setShowDepts] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const [pol, setPol] = useState({ max_offers: 1, lock_placed: true, dream_multiplier: 1.5, auto_withdraw_on_place: true });
  const [polBusy, setPolBusy] = useState(false);
  const load = () => getMyCollege().then(({ data, status }) => {
    if (status === 404) return;
    setCo(data);
    setF({ name: data.name || "", website: data.website || "", about: data.about || "" });
    const p = data.placement_policy || {};
    setPol({
      max_offers: p.max_offers ?? 1, lock_placed: p.lock_placed ?? true,
      dream_multiplier: p.dream_multiplier ?? 1.5, auto_withdraw_on_place: p.auto_withdraw_on_place ?? true,
    });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const savePolicy = async () => {
    setPolBusy(true); setErr(""); setMsg(null);
    try {
      await updateCollegePolicy({
        max_offers: Number(pol.max_offers), lock_placed: pol.lock_placed,
        dream_multiplier: Number(pol.dream_multiplier), auto_withdraw_on_place: pol.auto_withdraw_on_place,
      });
      setMsg("Placement policy saved — enforced the moment students apply.");
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save policy."); }
    finally { setPolBusy(false); }
  };

  const saveIdentity = async (e) => {
    e.preventDefault();
    if (!f.name.trim()) { setErr("College name is required."); return; }
    setBusy(true); setErr(""); setMsg(null);
    try {
      await updateCollege({ name: f.name.trim(), website: f.website.trim(), about: f.about.trim() });
      setMsg("Profile saved."); load();
    } catch (ex) { setErr(ex?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  const changeLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true); setErr(""); setMsg(null);
    try { await setCollegeLogo(await fileToLogoDataUrl(file)); setMsg("Logo updated."); load(); }
    catch (ex) { setErr(ex?.response?.data?.detail || ex.message || "Could not upload logo."); }
    finally { setLogoBusy(false); }
  };

  const dropLogo = async () => {
    setLogoBusy(true); setErr(""); setMsg(null);
    try { await removeCollegeLogo(); setMsg("Logo removed."); load(); } catch (_) {}
    finally { setLogoBusy(false); }
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(co.invite_code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };

  const rotateCode = async () => {
    if (!(await confirm({ title: "Please confirm", description: "Generate a new invite code? The old code stops working — students already joined stay linked.", danger: true }))) return;
    setCodeBusy(true); setErr(""); setMsg(null);
    try { await regenerateInviteCode(); setMsg("New invite code generated."); load(); }
    catch (ex) { setErr(ex?.response?.data?.detail || "Could not regenerate."); }
    finally { setCodeBusy(false); }
  };

  if (!co) return <div className="container-x py-16 text-stone-500 flex items-center gap-2"><Loader2 size={18} className="animate-spin" /> Loading…</div>;

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Profile</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Your college, everywhere it shows.</h1>
      <p className="mt-2 text-stone-600 max-w-xl">What you set here appears to students when they join, on their dashboards, and on exported placement reports.</p>

      {msg && <p className="mt-5 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5" data-testid="profile-msg">{msg}</p>}
      {err && <p className="mt-5 text-sm rounded-xl bg-red-50 border border-red-200 text-red-700 px-4 py-2.5">{err}</p>}
      {showDepts && <DepartmentsModal onClose={() => setShowDepts(false)} />}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* ── Identity ── */}
        <form onSubmit={saveIdentity} className="rounded-3xl border border-stone-200 bg-white p-7 space-y-4">
          <h2 className="font-display text-2xl mb-1">Identity</h2>
          <div className="flex items-center gap-5">
            {co.logo ? (
              <img src={co.logo} alt={co.name} data-testid="profile-logo" className="w-20 h-20 rounded-2xl object-contain bg-stone-50 border border-stone-200" />
            ) : (
              <span className="w-20 h-20 rounded-2xl bg-stone-100 border border-dashed border-stone-300 flex items-center justify-center text-stone-400"><GraduationCap size={28} /></span>
            )}
            <div className="space-y-2">
              <label className="btn-ghost text-sm cursor-pointer inline-flex">
                {logoBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {co.logo ? "Change logo" : "Upload logo"}
                <input type="file" accept="image/*" onChange={changeLogo} data-testid="profile-logo-input" className="hidden" />
              </label>
              {co.logo && (
                <button type="button" onClick={dropLogo} disabled={logoBusy} className="block text-xs text-stone-400 hover:text-red-600">Remove logo</button>
              )}
            </div>
          </div>
          <Field label="College / institution name *">
            <input value={f.name} onChange={set("name")} data-testid="profile-name" className="cm-inp" />
          </Field>
          <Field label="Website">
            <input value={f.website} onChange={set("website")} data-testid="profile-website" placeholder="https://yourcollege.edu" className="cm-inp" />
          </Field>
          <Field label="About">
            <textarea value={f.about} onChange={set("about")} rows={3} placeholder="A short line about your institution." className="cm-inp" />
          </Field>
          <button type="submit" disabled={busy} data-testid="profile-save" className="btn-primary disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save profile
          </button>
        </form>

        {/* ── Right rail: invite code + structure ── */}
        <div className="space-y-6">
          <div className="rounded-3xl border border-stone-200 bg-white p-6">
            <span className="overline">Student invite code</span>
            <p className="font-mono text-3xl tracking-[0.2em] mt-3 text-stone-900" data-testid="profile-code">{co.invite_code}</p>
            <p className="text-xs text-stone-500 mt-2">Students enter this at signup to link to your placement cell.</p>
            <div className="mt-4 flex gap-2">
              <button onClick={copyCode} className="btn-ghost text-sm flex-1 justify-center">
                {copied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
              <button onClick={rotateCode} disabled={codeBusy} data-testid="profile-rotate" className="btn-ghost text-sm flex-1 justify-center disabled:opacity-60">
                {codeBusy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Regenerate
              </button>
            </div>
          </div>

          <div className="rounded-3xl border border-stone-200 bg-white p-6">
            <span className="overline">Structure</span>
            <p className="text-sm text-stone-600 mt-2">Departments drive who can apply to which drive, and power department-scoped HOD access.</p>
            <button onClick={() => setShowDepts(true)} data-testid="profile-depts" className="btn-ghost text-sm mt-4 w-full justify-center"><Layers size={15} /> Manage departments</button>
            <Link to="/campus/team" className="btn-ghost text-sm mt-2 w-full justify-center"><UserPlus size={15} /> Manage team & HODs</Link>
          </div>

          <div className="rounded-3xl border border-stone-200 bg-white p-6" data-testid="profile-policy">
            <span className="overline">Placement policy</span>
            <p className="text-xs text-stone-500 mt-1.5">Enforced automatically the moment a student applies — no more spreadsheet policing.</p>
            <div className="mt-4 space-y-3">
              <label className="flex items-center justify-between text-sm">
                <span className="text-stone-700">Max offers per student</span>
                <input type="number" min="1" max="10" value={pol.max_offers} onChange={(e) => setPol({ ...pol, max_offers: e.target.value })}
                  data-testid="policy-max-offers" className="w-16 px-2 py-1 rounded-lg border border-stone-300 text-sm font-mono text-right" />
              </label>
              <label className="flex items-center justify-between text-sm cursor-pointer">
                <span className="text-stone-700">Lock placed students out of the pool</span>
                <input type="checkbox" checked={pol.lock_placed} onChange={(e) => setPol({ ...pol, lock_placed: e.target.checked })}
                  data-testid="policy-lock" className="w-4 h-4 accent-[#C2410C]" />
              </label>
              <label className="flex items-center justify-between text-sm">
                <span className="text-stone-700">"Dream" drive if package ≥</span>
                <span className="inline-flex items-center gap-1">
                  <input type="number" step="0.1" min="1" max="5" value={pol.dream_multiplier} onChange={(e) => setPol({ ...pol, dream_multiplier: e.target.value })}
                    data-testid="policy-dream" className="w-14 px-2 py-1 rounded-lg border border-stone-300 text-sm font-mono text-right" />
                  <span className="text-stone-500 text-xs">× current offer</span>
                </span>
              </label>
              <label className="flex items-center justify-between text-sm cursor-pointer">
                <span className="text-stone-700">Auto-withdraw pending apps on placement</span>
                <input type="checkbox" checked={pol.auto_withdraw_on_place} onChange={(e) => setPol({ ...pol, auto_withdraw_on_place: e.target.checked })}
                  data-testid="policy-withdraw" className="w-4 h-4 accent-[#C2410C]" />
              </label>
            </div>
            <button onClick={savePolicy} disabled={polBusy} data-testid="policy-save" className="btn-primary text-sm mt-4 w-full justify-center disabled:opacity-60">
              {polBusy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save policy
            </button>
          </div>

          <PlacementHistoryCard college={co} />

          <CollegeCertificateSettings />

          <div className="rounded-3xl border border-stone-200 bg-white p-6" data-testid="profile-modules">
            <span className="overline">Workspace modules</span>
            <p className="text-xs text-stone-500 mt-1.5">Enabled by Wishire for your institution — contact your account manager to add modules.</p>
            {co.modules && (
              <ul className="mt-4 space-y-2">
                {[["students", "Students"], ["drives", "Drives"], ["announcements", "Announcements"], ["team", "Team"], ["reports", "Reports & NIRF"], ["recruiters", "Recruiter CRM"], ["ai_officer", "AI Officer"]].map(([k, label]) => (
                  <li key={k} className="flex items-center justify-between text-sm">
                    <span className={co.modules[k] ? "text-stone-800" : "text-stone-400"}>{label}</span>
                    {co.modules[k]
                      ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><Check size={11} /> Enabled</span>
                      : <span className="text-xs font-medium text-stone-400 border border-stone-200 px-2 py-0.5 rounded-full">Locked</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Prior-season placement figures — fills the 3-year NIRF trend table in the
   government report. Up to 3 previous academic years. */
function PlacementHistoryCard({ college }) {
  const empty = { year: "", placeable: "", placed: "", median_lpa: "" };
  const [rows, setRows] = useState(() => {
    const h = (college?.historical_placements || []).map((x) => ({
      year: x.year || "", placeable: x.placeable ?? "", placed: x.placed ?? "", median_lpa: x.median_lpa ?? "",
    }));
    while (h.length < 3) h.push({ ...empty });
    return h.slice(0, 3);
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (i, k) => (e) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: e.target.value } : row)));

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = rows.filter((r) => r.year.trim()).map((r) => ({
        year: r.year.trim(), placeable: Number(r.placeable) || 0, placed: Number(r.placed) || 0,
        median_lpa: r.median_lpa === "" ? null : Number(r.median_lpa),
      }));
      await updateCollege({ historical_placements: payload });
      setMsg("Saved — the report's three-year table now includes these seasons.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not save."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-3xl border border-stone-200 bg-white p-6" data-testid="profile-history">
      <span className="overline">Placement history</span>
      <p className="text-xs text-stone-500 mt-1.5">Previous seasons for the NIRF three-year statement in your government report. Current season is computed live.</p>
      <div className="mt-4 space-y-2">
        <div className="grid grid-cols-4 gap-2 text-[10px] font-mono uppercase tracking-widest text-stone-400 px-1">
          <span>Year</span><span>Placeable</span><span>Placed</span><span>Median LPA</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-4 gap-2">
            <input value={r.year} onChange={set(i, "year")} placeholder="2023-24" data-testid={`hist-year-${i}`}
              className="w-full min-w-0 px-2.5 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
            <input type="number" min="0" value={r.placeable} onChange={set(i, "placeable")} placeholder="480"
              className="w-full min-w-0 px-2.5 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
            <input type="number" min="0" value={r.placed} onChange={set(i, "placed")} placeholder="350"
              className="w-full min-w-0 px-2.5 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
            <input type="number" min="0" step="0.1" value={r.median_lpa} onChange={set(i, "median_lpa")} placeholder="4.5"
              className="w-full min-w-0 px-2.5 py-1.5 rounded-lg border border-stone-300 text-sm font-mono" />
          </div>
        ))}
      </div>
      {msg && <p className="text-xs mt-3 text-stone-600">{msg}</p>}
      <button onClick={save} disabled={busy} data-testid="hist-save" className="btn-primary text-sm mt-4 w-full justify-center disabled:opacity-60">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save history
      </button>
    </div>
  );
}

/* Billing UI intentionally removed — colleges never see pricing.
   Plans and dashboard modules are assigned from the Wishire admin panel. */

function VerifyToggle({ student, onChange }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try { await verifyStudent(student.user_id, !student.cgpa_verified); onChange?.(); }
    catch (_) {} finally { setBusy(false); }
  };
  return (
    <button onClick={toggle} disabled={busy} data-testid={`cm-verify-${student.user_id}`}
      title={student.cgpa_verified ? "Verified — click to unverify" : "Mark CGPA/documents verified"}
      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${student.cgpa_verified ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-stone-300 text-stone-500 hover:border-stone-900"}`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : <ShieldCheck size={12} />} {student.cgpa_verified ? "Verified" : "Verify"}
    </button>
  );
}

const PLACEMENT_STATUS_STYLE = {
  placeable: "border-stone-300 text-stone-600 bg-white",
  higher_studies: "border-purple-300 text-purple-700 bg-purple-50",
  opted_out: "border-amber-300 text-amber-700 bg-amber-50",
};
function PlacementStatusSelect({ student, onChange }) {
  const [val, setVal] = useState(student.placement_status || "placeable");
  const change = async (e) => {
    const next = e.target.value;
    setVal(next);
    try { await setPlacementStatus(student.user_id, next); onChange?.(); } catch (_) { setVal(student.placement_status || "placeable"); }
  };
  return (
    <select value={val} onChange={change} data-testid={`cm-pstatus-${student.user_id}`}
      title="Only 'placeable' students count in placement %"
      className={`text-xs font-medium px-2 py-1 rounded-full border cursor-pointer ${PLACEMENT_STATUS_STYLE[val] || PLACEMENT_STATUS_STYLE.placeable}`}>
      <option value="placeable">Placeable</option>
      <option value="higher_studies">Higher studies</option>
      <option value="opted_out">Opted out</option>
    </select>
  );
}

/* ─────────── Placement team ─────────── */
export function CampusTeam() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", member_role: "coordinator", branch: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const load = () => listTeam().then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setMsg("Name and email are required."); return; }
    if (form.member_role === "hod" && !form.branch.trim()) { setMsg("An HOD needs a department (branch)."); return; }
    setBusy(true); setMsg(null);
    try {
      await inviteMember({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        member_role: form.member_role,
        branch: form.branch.trim() || undefined,
      });
      setMsg(`Invited ${form.email.trim()} as ${form.member_role === "hod" ? `HOD of ${form.branch.trim()}` : "coordinator"}. They set a password via "Forgot password".`);
      setForm({ name: "", email: "", member_role: "coordinator", branch: "" });
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Could not invite."); }
    finally { setBusy(false); }
  };
  const remove = async (m) => {
    if (!(await confirm({ title: "Please confirm", description: `Remove ${m.name || m.email} from the placement team?`, danger: true }))) return;
    try { await removeMember(m.user_id); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove."); }
  };

  return (
    <div className="container-x py-10 md:py-14">
      <span className="overline">Team</span>
      <h1 className="font-display text-4xl md:text-5xl mt-2">Your placement team.</h1>
      <p className="text-stone-500 mt-2 max-w-lg">Invite branch or faculty coordinators to help run drives, verify students, and shortlist.</p>

      <form onSubmit={invite} className="mt-8 rounded-3xl border border-stone-200 bg-white p-6 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end max-w-4xl">
        <div><label className="overline">Name</label><input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="cm-team-name" className="cm-inp mt-1.5" placeholder="Member name" /></div>
        <div><label className="overline">Email</label><input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="cm-team-email" className="cm-inp mt-1.5" placeholder="member@college.edu" /></div>
        <div>
          <label className="overline">Role</label>
          <select value={form.member_role} onChange={(e) => setForm((f) => ({ ...f, member_role: e.target.value }))} data-testid="cm-team-role" className="cm-inp mt-1.5">
            <option value="coordinator">Coordinator · full access</option>
            <option value="hod">HOD · own department only</option>
          </select>
        </div>
        <div><label className="overline">{form.member_role === "hod" ? "Department *" : "Branch (optional)"}</label><input value={form.branch} onChange={(e) => setForm((f) => ({ ...f, branch: e.target.value }))} data-testid="cm-team-branch" className="cm-inp mt-1.5" placeholder="CSE" /></div>
        <button type="submit" disabled={busy} data-testid="cm-team-invite" className="btn-primary justify-center disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <><UserPlus size={15} /> Invite</>}</button>
      </form>
      <p className="mt-3 text-xs text-stone-500 max-w-4xl">HODs see and manage only their own department — students, readiness, verification and nudges are scoped to their branch. Coordinators and the owner see the whole college.</p>
      {msg && <p className="mt-4 text-sm rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2.5 max-w-3xl">{msg}</p>}

      <div className="mt-8 max-w-3xl space-y-3">
        {rows === null ? <p className="text-stone-500">Loading…</p> : rows.map((m) => (
          <div key={m.user_id} className="rounded-2xl border border-stone-200 bg-white p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-800 flex items-center justify-center font-display">{(m.name || "?")[0]}</div>
              <div>
                <p className="font-medium">{m.name} {m.branch && <span className="text-xs text-stone-500">· {m.branch}</span>}</p>
                <p className="text-xs text-stone-500">{m.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-xs font-mono uppercase tracking-widest px-2.5 py-1 rounded-full ${m.member_role === "owner" ? "bg-stone-900 text-white" : "bg-stone-100 text-stone-600"}`}>{m.member_role}</span>
              {m.member_role !== "owner" && (
                <button onClick={() => remove(m)} data-testid={`cm-team-remove-${m.user_id}`} className="p-1.5 rounded-lg text-stone-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={15} /></button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="overline">{label}</label><div className="mt-1.5">{children}</div></div>;
}
